/**
 * NetworkClubProfileHub
 * Hoofdcomponent voor netwerkclub management
 * Gebaseerd op ProfileHub layout
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import EventInvitePanel from './components/EventInvitePanel';
import ClubMeetingPlanner from './components/ClubMeetingPlanner';
import { auth, db, storage } from '../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressForCover } from '../../utils/imageCompressor';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useEntityContext } from '../../hooks/useEntityContext';
import { EntitySwitcher } from '../roles/EntitySwitcher';
import { networkClubService } from '../../services/networkClubService';
import { NetworkClub, NetworkClubGuest } from '../../types/networkclub';
import { ClubInviteMembersModal, type ClubInviteMemberRow } from './ClubInviteMembersModal';
import { resolveHubCapabilities } from '../../types/entityMemberCapabilities';
import { Icon } from '../icons';
import { 
  Building2, Users, User, UserPlus, Settings, MapPin, Mail, Phone, Globe, Calendar, 
  CheckCircle, XCircle, Loader, Camera, Edit2, Save, X, Video, Info, Share2, Monitor
} from '../icons';
import { PostRespectArea } from '../respect';
import { PostInteractions } from '../PostInteractions';
import { connectionRequestService } from '../../services/connectionRequestService';
import InviteGuestModal from './components/InviteGuestModal';
import RequestClubVisit from './components/RequestClubVisit';
import VisitRequestManager from './components/VisitRequestManager';
import NetworkClubMeeting from './NetworkClubMeeting';
import PhysicalPresentationSetup from './components/PhysicalPresentationSetup';
import MeetingCountdown from './components/MeetingCountdown';
import ClubVideosSection from './components/ClubVideosSection';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, setDoc, deleteDoc, onSnapshot, orderBy, serverTimestamp, limit, deleteField } from 'firebase/firestore';
import { withAlpha, lighten, darken } from '../../utils/colorUtils';
import {
  CompanyIcon, MapPinIcon, LockIcon, GlobeIcon, MicrophoneIcon, EndCallIcon,
  AnnouncementIcon, CalendarIcon, InviteIcon, SettingsIcon as PhosphorSettings,
  ConnectionLinkIcon, EditIcon, LaunchIcon, CelebrationIcon, ActivationIcon,
  CommunityIcon as PhosphorUsers, VideoIcon, RelationIcon, ProjectorScreenIcon,
} from '../icons/phosphorSystem';
import { Capacitor } from '@capacitor/core';
import { normalizeNetworkClubWebsiteUrl } from '../../utils/networkClubWebsite';
import { openExternalUrl } from '../../utils/openExternalUrl';
import { generateClubSlug } from '../../utils/clubSlug';

/*  Besloten feed: minimale actiebalk  */
const GroupFeedActions: React.FC<{
  post: any;
  onShareToNewsfeed: (post: any) => Promise<void>;
}> = ({ post, onShareToNewsfeed }) => {
  const [showSocial, setShowSocial] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [shared, setShared] = React.useState(false);

  const handleShareNewsfeed = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      await onShareToNewsfeed(post);
      setShared(true);
      setTimeout(() => setShared(false), 3000);
    } finally {
      setSharing(false);
    }
  };

  const postText = String(post?.content || post?.text || post?.message || '').trim();

  return (
    <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
      <button
        onClick={handleShareNewsfeed}
        disabled={sharing || shared}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-60"
        style={shared
          ? { backgroundColor: '#e6f0ee', color: '#4F6660' }
          : { backgroundColor: '#f0f4f3', color: '#4F6660' }}
        onMouseEnter={(e) => { if (!sharing && !shared) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d6e4e0'; }}
        onMouseLeave={(e) => { if (!sharing && !shared) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f0f4f3'; }}
      >
        <Share2 size={13} strokeWidth={1.8} />
        {shared ? 'Gedeeld naar nieuwsfeed' : sharing ? 'Delen\u2026' : 'Deel naar nieuwsfeed'}
      </button>

      <button
        onClick={() => setShowSocial(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
        style={{ backgroundColor: '#f0f4f3', color: '#4F6660' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d6e4e0'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f0f4f3'; }}
      >
        <Globe size={13} strokeWidth={1.8} />
        Social media
      </button>

      {showSocial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowSocial(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-gray-900">Social media delen</p>
              <button onClick={() => setShowSocial(false)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100">
                <X size={14} strokeWidth={2} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4 line-clamp-3 bg-gray-50 rounded-lg px-3 py-2">{postText || 'Post inhoud'}</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'LinkedIn', bg: '#0077B5', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&summary=${encodeURIComponent(postText.slice(0, 300))}` },
                { label: 'Twitter / X', bg: '#000', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(postText.slice(0, 280))}` },
                { label: 'Facebook', bg: '#1877F2', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
                { label: 'WhatsApp', bg: '#25D366', url: `https://wa.me/?text=${encodeURIComponent(postText.slice(0, 400))}` },
              ].map(({ label, bg, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 h-9 rounded-lg text-white text-xs font-semibold transition-opacity hover:opacity-90"
                  style={{ backgroundColor: bg }}
                  onClick={() => setShowSocial(false)}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Land → vlag emoji ────────────────────────────────────────────────────────
// Converteert een landnaam (NL of volledig) naar de bijbehorende vlag-emoji.
// Lijst bevat de meest voorkomende landen in de Europese / internationale context.
const COUNTRY_FLAG_MAP: Record<string, string> = {
  // NL-namen
  'nederland':          '🇳🇱',
  'belgie':             '🇧🇪',
  'belgië':             '🇧🇪',
  'duitsland':          '🇩🇪',
  'france':             '🇫🇷',
  'frankrijk':          '🇫🇷',
  'verenigd koninkrijk':'🇬🇧',
  'engeland':           '🇬🇧',
  'united kingdom':     '🇬🇧',
  'groot brittannie':   '🇬🇧',
  'spanje':             '🇪🇸',
  'spain':              '🇪🇸',
  'italië':             '🇮🇹',
  'italie':             '🇮🇹',
  'italy':              '🇮🇹',
  'portugal':           '🇵🇹',
  'zweden':             '🇸🇪',
  'sweden':             '🇸🇪',
  'noorwegen':          '🇳🇴',
  'norway':             '🇳🇴',
  'denemarken':         '🇩🇰',
  'denmark':            '🇩🇰',
  'zwitserland':        '🇨🇭',
  'switzerland':        '🇨🇭',
  'oostenrijk':         '🇦🇹',
  'austria':            '🇦🇹',
  'polen':              '🇵🇱',
  'poland':             '🇵🇱',
  'verenigde staten':   '🇺🇸',
  'united states':      '🇺🇸',
  'usa':                '🇺🇸',
  'canada':             '🇨🇦',
  'australie':          '🇦🇺',
  'australië':          '🇦🇺',
  'australia':          '🇦🇺',
  'china':              '🇨🇳',
  'japan':              '🇯🇵',
  'india':              '🇮🇳',
  'brazilië':           '🇧🇷',
  'brazilie':           '🇧🇷',
  'brazil':             '🇧🇷',
  'suriname':           '🇸🇷',
  'marokko':            '🇲🇦',
  'morocco':            '🇲🇦',
  'turkije':            '🇹🇷',
  'turkey':             '🇹🇷',
  'griekenland':        '🇬🇷',
  'greece':             '🇬🇷',
  'luxemburg':          '🇱🇺',
  'luxembourg':         '🇱🇺',
  'ierland':            '🇮🇪',
  'ireland':            '🇮🇪',
  'nl':                 '🇳🇱',
  'be':                 '🇧🇪',
  'de':                 '🇩🇪',
  'fr':                 '🇫🇷',
  'gb':                 '🇬🇧',
  'uk':                 '🇬🇧',
  'es':                 '🇪🇸',
  'it':                 '🇮🇹',
  'pt':                 '🇵🇹',
  'se':                 '🇸🇪',
  'no':                 '🇳🇴',
  'dk':                 '🇩🇰',
  'ch':                 '🇨🇭',
  'at':                 '🇦🇹',
  'pl':                 '🇵🇱',
  'us':                 '🇺🇸',
  'ca':                 '🇨🇦',
  'au':                 '🇦🇺',
};

function countryToFlag(country: string): string | undefined {
  if (!country) return undefined;
  const key = country.trim().toLowerCase()
    .replace(/[^a-z\u00e0-\u00ff ]/g, '')
    .trim();
  return COUNTRY_FLAG_MAP[key];
}

function getGmtOffset(): string {
  try {
    const parts = new Intl.DateTimeFormat('nl', { timeZoneName: 'shortOffset' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || 'GMT+1';
  } catch { return 'GMT+1'; }
}

const ROLE_SORT_MEMBERS: Record<string, number> = { owner: 0, board: 1, organizer: 2, va: 3, member: 4 };

const NetworkClubProfileHub: React.FC = () => {
  const [user] = useAuthState(auth);
  const { entities, activeEntityId, setActiveEntityId } = useEntityContext(user?.uid);
  const activeEntity = entities.find(e => e.id === activeEntityId && (e.type === 'networkclub' || e.type === 'mastermind' || e.type === 'community'));
  
  const [club, setClub] = useState<NetworkClub | null>(null);
  const [members, setMembers] = useState<ClubInviteMemberRow[]>([]);
  const [guests, setGuests] = useState<NetworkClubGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'mijnverhaal' | 'overview' | 'mijnpitch' | 'members' | 'guests' | 'videos' | 'agenda' | 'bijeenkomst' | 'invite' | 'settings'>('mijnverhaal');
  /** Landings-tab: clubverhaal vs embedded externe site */
  const [clubLandingView, setClubLandingView] = useState<'overview' | 'story' | 'website'>('overview');
  const [showInviteGuestModal, setShowInviteGuestModal] = useState(false);
  const [showRequestVisitModal, setShowRequestVisitModal] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);
  const [physicalSetupOpen, setPhysicalSetupOpen] = useState(false);
  const [showPhysicalCountdown, setShowPhysicalCountdown] = useState(false);
  const [meetingRoom, setMeetingRoom] = useState<string | null>(null);
  const [autoOpenPresentation, setAutoOpenPresentation] = useState(false);
  const [meetingIsPhysical, setMeetingIsPhysical] = useState(false);
  const [clubEvents, setClubEvents] = useState<any[]>([]);
  const [selectedAgendaMonth, setSelectedAgendaMonth] = useState<string>('all');
  const [nextMeetingRsvp, setNextMeetingRsvp] = useState<'yes' | 'no' | null>(null);
  const [nextMeetingReason, setNextMeetingReason] = useState('');
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [savingNextMeetingRsvp, setSavingNextMeetingRsvp] = useState(false);

  // Cover image upload + drag-to-reposition
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');
  const [coverPosition, setCoverPosition] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const coverPositionRef = useRef<{ x: number; y: number }>({ x: 50, y: 50 });
  const [isCoverDragging, setIsCoverDragging] = useState(false);
  const [isCoverEditMode, setIsCoverEditMode] = useState(false);
  const [isPageEditMode, setIsPageEditMode] = useState(false);
  const [savingCover, setSavingCover] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverFrameRef = useRef<HTMLDivElement>(null);

  // Visit requests (bezoekers)
  const [visitRequests, setVisitRequests] = useState<any[]>([]);
  const [selectedVisitorMonth, setSelectedVisitorMonth] = useState<string>('all');

  const loadVisitRequests = useCallback(async (clubId: string) => {
    try {
      const snap = await getDocs(query(collection(db, 'clubVisitRequests'), where('clubId', '==', clubId)));
      const rawRequests = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        visitDate: d.data().visitDate?.toDate() || new Date(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
      }));

      // Enrich visit rows with visitor expertise so organizers can review attendance by field.
      const uniqueVisitorIds = Array.from(
        new Set(rawRequests.map((req: any) => req.visitorId).filter((id: any) => typeof id === 'string' && id.length > 0))
      );
      const expertiseByVisitor = new Map<string, string[]>();

      await Promise.all(uniqueVisitorIds.map(async (visitorId) => {
        try {
          const userSnap = await getDoc(doc(db, 'users', visitorId));
          if (!userSnap.exists()) return;
          const userData: any = userSnap.data();
          const expertise = Array.isArray(userData?.expertise)
            ? userData.expertise
            : Array.isArray(userData?.onboardingData?.expertise)
              ? userData.onboardingData.expertise
              : [];
          expertiseByVisitor.set(
            visitorId,
            expertise
              .filter((item: any) => typeof item === 'string' && item.trim().length > 0)
              .map((item: string) => item.trim())
          );
        } catch {
          expertiseByVisitor.set(visitorId, []);
        }
      }));

      setVisitRequests(
        rawRequests
          .map((req: any) => ({
            ...req,
            visitorExpertise: expertiseByVisitor.get(req.visitorId) || [],
          }))
          .sort((a: any, b: any) => b.createdAt - a.createdAt)
      );
    } catch {}
  }, []);

  const loadClubEvents = useCallback(async (entityId: string) => {
    try {
      const snap = await getDocs(query(collection(db, 'clubEvents'), where('entityId', '==', entityId)));
      const parsed = snap.docs
        .map((d) => {
          const data: any = d.data();
          const eventDate = typeof data.date === 'string' ? new Date(`${data.date}T00:00:00`) : new Date();
          return {
            id: d.id,
            ...data,
            eventDate,
            createdAt: data.createdAt?.toDate?.() || new Date(),
          };
        })
        .filter((ev) => ev.eventDate instanceof Date && !Number.isNaN(ev.eventDate.getTime()))
        .sort((a, b) => a.eventDate.getTime() - b.eventDate.getTime());
      setClubEvents(parsed);
    } catch {
      setClubEvents([]);
    }
  }, []);

  useEffect(() => {
    if (club?.id) loadVisitRequests(club.id);
  }, [club?.id, loadVisitRequests]);

  const openUserProfile = (userId: string) => {
    if (!userId) return;
    window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId } }));
  };

  // Club feed / posts
  const [clubPosts, setClubPosts] = useState<any[]>([]);
  const [communityFeedPosts, setCommunityFeedPosts] = useState<any[]>([]);
  const [shareToast, setShareToast] = useState<string | null>(null);
  const [newPost, setNewPost] = useState('');
  const [postingPost, setPostingPost] = useState(false);

  // Active meeting (real-time)
  const [activeMeeting, setActiveMeeting] = useState<{ roomName: string; startedByName: string; startedAt: Date | null } | null>(null);

  // Online leden (aanwezig op de clubpagina)
  const [onlinePresences, setOnlinePresences] = useState<Array<{ userId: string; userName: string; userPhotoURL?: string; status: string; countryCode?: string }>>([]);
  const [accessCheckLoading, setAccessCheckLoading] = useState(true);
  const [hasClubAccess, setHasClubAccess] = useState(false);
  const [isApprovedGuest, setIsApprovedGuest] = useState(false);

  // Sync cover image URL + position whenever club changes
  useEffect(() => {
    if (!club) return;
    setCoverImageUrl((club as any).coverImageUrl || '');
    const pos = (club as any).coverPosition;
    if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
      const next = { x: Math.max(0, Math.min(100, pos.x)), y: Math.max(0, Math.min(100, pos.y)) };
      setCoverPosition(next);
      coverPositionRef.current = next;
    } else {
      const def = { x: 50, y: 50 };
      setCoverPosition(def);
      coverPositionRef.current = def;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club?.id, (club as any)?.coverImageUrl, (club as any)?.coverPosition?.x, (club as any)?.coverPosition?.y]);

  // Drag-to-reposition helpers
  const updateCoverPositionFromEvent = useCallback((clientX: number, clientY: number) => {
    const frame = coverFrameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const next = {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
    setCoverPosition(next);
    coverPositionRef.current = next;
  }, []);
  const canManageCover =
    activeEntity?.role === 'owner' || activeEntity?.role === 'organizer';

  useEffect(() => {
    if (!isCoverDragging || !canManageCover || !isCoverEditMode) return;
    const onMove = (e: MouseEvent) => updateCoverPositionFromEvent(e.clientX, e.clientY);
    const onUp = async () => {
      setIsCoverDragging(false);
      if (club) {
        try {
          await updateDoc(doc(db, 'networkClubs', club.id), {
            coverPosition: coverPositionRef.current,
          });
        } catch {}
      }
      setIsCoverEditMode(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageCover, isCoverDragging, isCoverEditMode, updateCoverPositionFromEvent]);

  useEffect(() => {
    const verifyAccess = async () => {
      if (!activeEntity) {
        setHasClubAccess(false);
        setIsApprovedGuest(false);
        setAccessCheckLoading(false);
        return;
      }

      if (!user) {
        setHasClubAccess(false);
        setIsApprovedGuest(false);
        setAccessCheckLoading(false);
        return;
      }

      const role = activeEntity.role;
      const isMemberRole = role === 'owner' || role === 'organizer' || role === 'board' || role === 'member';
      if (isMemberRole) {
        setHasClubAccess(true);
        setIsApprovedGuest(false);
        setAccessCheckLoading(false);
        return;
      }

      setAccessCheckLoading(true);
      try {
        const clubData = await networkClubService.getNetworkClubByEntityId(activeEntity.id);
        if (!clubData) {
          setHasClubAccess(false);
          setIsApprovedGuest(false);
          return;
        }

        const guestSnap = await getDocs(
          query(collection(db, 'networkClubGuests'), where('clubId', '==', clubData.id))
        );
        const userEmail = (user.email || '').toLowerCase().trim();
        const allowedGuestStatuses = new Set(['invited', 'confirmed', 'attended']);
        const allowedGuest = guestSnap.docs.some((d) => {
          const data: any = d.data();
          const status = typeof data?.status === 'string' ? data.status : '';
          const guestEmail = typeof data?.email === 'string' ? data.email.toLowerCase().trim() : '';
          return allowedGuestStatuses.has(status) && !!userEmail && guestEmail === userEmail;
        });

        setHasClubAccess(allowedGuest);
        setIsApprovedGuest(allowedGuest);
      } catch {
        setHasClubAccess(false);
        setIsApprovedGuest(false);
      } finally {
        setAccessCheckLoading(false);
      }
    };

    void verifyAccess();
  }, [activeEntity, user]);

  // Load club data + real-time active meeting listener + presence
  useEffect(() => {
    if (activeEntity && hasClubAccess) {
      loadClubData(activeEntity.id);
      loadClubEvents(activeEntity.id);

      // Registreer eigen aanwezigheid op de clubpagina
      if (user?.uid) {
        const presenceId = `${user.uid}_${activeEntity.id}`;
        void setDoc(doc(db, 'userPresence', presenceId), {
          userId: user.uid,
          userName: user.displayName || user.email?.split('@')[0] || 'Lid',
          userPhotoURL: user.photoURL || null,
          entityId: activeEntity.id,
          status: 'active',
          lastSeen: serverTimestamp(),
        }, { merge: true });

        // Periodiek aanwezigheid vernieuwen (elke 60 seconden)
        const heartbeat = setInterval(() => {
          void setDoc(doc(db, 'userPresence', presenceId), { lastSeen: serverTimestamp(), status: 'active' }, { merge: true });
        }, 60_000);

        // Vertrek markeren bij unmount
        const markAway = () => void setDoc(doc(db, 'userPresence', presenceId), { status: 'away', lastSeen: serverTimestamp() }, { merge: true });
        window.addEventListener('beforeunload', markAway);

        // Real-time: luister naar alle aanwezigen in deze club
        const presenceUnsub = onSnapshot(
          query(collection(db, 'userPresence'), where('entityId', '==', activeEntity.id)),
          async (snap) => {
            const fiveMinutesAgo = Date.now() - 5 * 60_000;
            const online = snap.docs
              .map(d => d.data() as { userId: string; userName: string; userPhotoURL?: string; status: string; lastSeen: any; countryCode?: string })
              .filter(p => p.status !== 'away' && (p.lastSeen?.toMillis?.() ?? 0) > fiveMinutesAgo);

            // Enrich met landcode uit userProfiles (voor vlag emoji)
            const enriched = await Promise.all(online.map(async (p) => {
              if (p.countryCode) return p; // al aanwezig in presence doc
              try {
                const profSnap = await getDoc(doc(db, 'userProfiles', p.userId));
                if (profSnap.exists()) {
                  const profData = profSnap.data() as any;
                  const country: string =
                    profData?.country ||
                    profData?.onboardingData?.country ||
                    '';
                  return { ...p, countryCode: countryToFlag(country) };
                }
              } catch {}
              return p;
            }));

            setOnlinePresences(enriched);
          },
          () => {},
        );

        // Real-time: watch activeMeetings so all members see the live banner
        const meetingUnsub = onSnapshot(
          doc(db, 'activeMeetings', activeEntity.id),
          (snap) => {
            if (snap.exists()) {
              const d = snap.data();
              setActiveMeeting({ roomName: d.roomName, startedByName: d.startedByName, startedAt: d.startedAt?.toDate?.() ?? null });
            } else {
              setActiveMeeting(null);
            }
          },
          () => setActiveMeeting(null),
        );

        return () => {
          clearInterval(heartbeat);
          window.removeEventListener('beforeunload', markAway);
          markAway();
          presenceUnsub();
          meetingUnsub();
        };
      }

      // Fallback als user null is — alleen meeting listener
      const unsub = onSnapshot(
        doc(db, 'activeMeetings', activeEntity.id),
        (snap) => {
          if (snap.exists()) {
            const d = snap.data();
            setActiveMeeting({ roomName: d.roomName, startedByName: d.startedByName, startedAt: d.startedAt?.toDate?.() ?? null });
          } else {
            setActiveMeeting(null);
          }
        },
        () => setActiveMeeting(null),
      );
      return () => unsub();
    }
  }, [activeEntity, hasClubAccess, loadClubEvents]);

  const loadClubData = async (entityId: string) => {
    setLoading(true);
    try {
      // Haal club op via entityId
      const clubData = await networkClubService.getNetworkClubByEntityId(entityId);
      if (clubData) {
        // Cover fields can come from different write paths:
        // - hub settings -> networkClubs/{clubId}
        // - groups manager -> entities/{entityId}
        // Merge with entity fallback so members always see the latest cover.
        const entitySnap = await getDoc(doc(db, 'entities', entityId));
        const entityData = entitySnap.exists() ? (entitySnap.data() as any) : null;
        const mergedClubData: any = {
          ...(clubData as any),
          coverImageUrl: (clubData as any).coverImageUrl || entityData?.coverImageUrl || '',
          coverPosition: (clubData as any).coverPosition || entityData?.coverPosition || { x: 50, y: 50 },
          logoUrl: (clubData as any).logoUrl || entityData?.logoUrl || '',
        };
        setClub(mergedClubData);
        
        // Load members (uit entityMembers)
        const membersQuery = query(
          collection(db, 'entityMembers'),
          where('entityId', '==', entityId)
        );
        const membersSnap = await getDocs(membersQuery);
        const membersData: ClubInviteMemberRow[] = [];

        for (const memberDoc of membersSnap.docs) {
          const memberData = memberDoc.data();
          const [userDocSnap, userProfileSnap] = await Promise.all([
            getDoc(doc(db, 'users', memberData.userId)),
            getDoc(doc(db, 'userProfiles', memberData.userId)),
          ]);
          const userData: any = userDocSnap.exists() ? userDocSnap.data() : {};
          const profileData: any = userProfileSnap.exists() ? userProfileSnap.data() : {};
          const fallbackNameFromEmail = (userData.email || profileData.email || memberData.email || '')
            .toString()
            .split('@')[0]
            .trim();
          const resolvedName = (
            userData.displayName ||
            profileData.displayName ||
            userData.name ||
            profileData.name ||
            memberData.displayName ||
            memberData.name ||
            fallbackNameFromEmail ||
            'Lid'
          ).toString().trim();
          const resolvedEmail = (userData.email || profileData.email || memberData.email || '').toString();
          const resolvedPhoto =
            (userData.photoURL || profileData.photoURL || memberData.photoURL || '').toString();
          const country =
            (profileData.country || userData.country || userData.onboardingData?.country || '').toString() || undefined;

          membersData.push({
            id: memberData.userId,
            clubId: mergedClubData.id,
            entityId: entityId,
            name: resolvedName || 'Lid',
            email: resolvedEmail,
            photoURL: resolvedPhoto || undefined,
            role: memberData.role || 'member',
            joinedAt: memberData.createdAt?.toDate() || new Date(),
            status: 'active',
            country,
            timezone: getGmtOffset(),
            capabilities:
              memberData.capabilities && typeof memberData.capabilities === 'object'
                ? memberData.capabilities
                : memberData.capabilities === null
                  ? null
                  : undefined,
          });
        }
        membersData.sort(
          (a, b) =>
            (ROLE_SORT_MEMBERS[a.role] ?? 9) - (ROLE_SORT_MEMBERS[b.role] ?? 9) ||
            a.name.localeCompare(b.name, 'nl'),
        );
        setMembers(membersData);
        
        // Load guests (uit networkClubGuests)
        const guestsQuery = query(
          collection(db, 'networkClubGuests'),
          where('clubId', '==', mergedClubData.id)
        );
        const guestsSnap = await getDocs(guestsQuery);
        const guestsData: NetworkClubGuest[] = guestsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          invitedAt: doc.data().invitedAt?.toDate() || new Date()
        } as NetworkClubGuest));
        setGuests(guestsData);
      }
    } catch (error) {
      console.error('Error loading club data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  //  Meeting functions 
  const startOnlineMeeting = async () => {
    if (!activeEntity || !user) return;
    const roomName = `club-${activeEntity.id}-${Date.now()}`;
    setMeetingRoom(roomName);
    setAutoOpenPresentation(false);
    setMeetingIsPhysical(false);
    // Eerst Firestore: `NetworkClubMeeting` zet modus via `meetingSettings`-snapshot (`meetingMode` ontbreekt daar → altijd 'online' → Jitsi).
    await Promise.all([
      setDoc(doc(db, 'activeMeetings', activeEntity.id), {
        roomName,
        entityId: activeEntity.id,
        startedBy: user.uid,
        startedByName: user.displayName || user.email || 'Organisator',
        startedAt: serverTimestamp(),
        meetingMode: 'online',
      }),
      setDoc(
        doc(db, 'meetingSettings', activeEntity.id),
        { meetingMode: 'online', updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      ),
    ]);
    setShowMeeting(true);
  };

  const startPhysicalMeeting = async () => {
    if (!activeEntity || !user) return;
    const roomName = `club-${activeEntity.id}-${Date.now()}`;
    setMeetingRoom(roomName);
    setAutoOpenPresentation(false);
    setMeetingIsPhysical(true);
    await Promise.all([
      setDoc(doc(db, 'activeMeetings', activeEntity.id), {
        roomName,
        entityId: activeEntity.id,
        startedBy: user.uid,
        startedByName: user.displayName || user.email || 'Organisator',
        startedAt: serverTimestamp(),
        meetingMode: 'physical',
      }),
      setDoc(
        doc(db, 'meetingSettings', activeEntity.id),
        { meetingMode: 'physical', updatedAt: serverTimestamp(), updatedBy: user.uid },
        { merge: true },
      ),
    ]);
    setShowMeeting(true);
  };

  const handleLeaveMeeting = async () => {
    setShowMeeting(false);
    setMeetingRoom(null);
    setMeetingIsPhysical(false);
    if (activeEntity) {
      await deleteDoc(doc(db, 'activeMeetings', activeEntity.id)).catch(() => {});
    }
  };

  // Cover image upload handler (compressed via shared utility)
  const handleCoverUpload = useCallback(async (file: File) => {
    if (!activeEntity || !club) return;
    if (!file.type.startsWith('image/')) {
      alert('Kies een afbeeldingsbestand.');
      return;
    }
    setSavingCover(true);
    try {
      const compressed = await compressForCover(file);
      const fileRef = storageRef(storage, `groupHeaders/${activeEntity.id}/${Date.now()}-${compressed.name}`);
      await uploadBytes(fileRef, compressed);
      const url = await getDownloadURL(fileRef);
      setCoverImageUrl(url);
      await updateDoc(doc(db, 'networkClubs', club.id), {
        coverImageUrl: url,
        coverPosition: coverPositionRef.current,
      });
    } catch (error: any) {
      console.error('Cover upload failed:', error);
      alert('Omslagfoto uploaden mislukt. Probeer opnieuw.');
    } finally {
      setSavingCover(false);
    }
  }, [activeEntity, club]);

  const handleLogoUpload = useCallback(async (file: File) => {
    if (!activeEntity || !club) return;
    if (!file.type.startsWith('image/')) return;
    setSavingLogo(true);
    try {
      const compressed = await compressForCover(file);
      const fileRef = storageRef(storage, `groupHeaders/${activeEntity.id}/logo_${Date.now()}-${compressed.name}`);
      await uploadBytes(fileRef, compressed);
      const url = await getDownloadURL(fileRef);
      await updateDoc(doc(db, 'networkClubs', club.id), { logoUrl: url });
      setClub((prev: any) => prev ? { ...prev, logoUrl: url } : prev);
    } catch (error: any) {
      console.error('Logo upload failed:', error);
      alert('Logo uploaden mislukt. Probeer opnieuw.');
    } finally {
      setSavingLogo(false);
    }
  }, [activeEntity, club]);

  // Load club-specific feed posts
  const loadClubPosts = useCallback(async (clubId: string) => {
    const normalizePosts = (docs: any[]) =>
      docs
        .map((d: any) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
        }))
        .sort((a: any, b: any) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));

    try {
      // Primary query with server-side ordering (fastest when index exists).
      const orderedSnap = await getDocs(
        query(collection(db, 'networkClubNews'), where('clubId', '==', clubId), orderBy('createdAt', 'desc'))
      );
      setClubPosts(normalizePosts(orderedSnap.docs));
    } catch (orderedError) {
      // Fallback when composite index is missing: load by clubId, sort in client.
      try {
        const fallbackSnap = await getDocs(
          query(collection(db, 'networkClubNews'), where('clubId', '==', clubId))
        );
        setClubPosts(normalizePosts(fallbackSnap.docs));
      } catch (fallbackError) {
        console.error('Kon club updates niet laden:', fallbackError || orderedError);
        setClubPosts([]);
      }
    }
  }, []);

  useEffect(() => {
    if (club?.id) loadClubPosts(club.id);
  }, [club?.id, loadClubPosts]);

  // Realtime listener voor besloten feed (vervangt getDocs zodat nieuwe posts direct zichtbaar zijn)
  useEffect(() => {
    const eid = activeEntity?.id;
    if (!eid) { setCommunityFeedPosts([]); return; }

    const normalizePost = (d: any) => {
      const data = typeof d.data === 'function' ? d.data() : d;
      return {
        id: d.id || data.id,
        ...data,
        createdAt: data?.timestamp?.toDate?.() || data?.createdAt?.toDate?.() || new Date(),
      };
    };

    // Primary: with orderBy (snelst als index bestaat)
    let unsub: (() => void) | null = null;
    try {
      unsub = onSnapshot(
        query(
          collection(db, 'communityPosts'),
          where('entityId', '==', eid),
          orderBy('timestamp', 'desc'),
          limit(50)
        ),
        (snap) => {
          setCommunityFeedPosts(snap.docs.map(normalizePost));
        },
        () => {
          // Fallback zonder orderBy als index ontbreekt
          unsub = onSnapshot(
            query(collection(db, 'communityPosts'), where('entityId', '==', eid)),
            (snap) => {
              const posts = snap.docs
                .map(normalizePost)
                .sort((a: any, b: any) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
              setCommunityFeedPosts(posts);
            },
            (err) => {
              console.error('Besloten feed listener fout:', err);
              setCommunityFeedPosts([]);
            }
          );
        }
      );
    } catch (err) {
      console.error('Besloten feed setup fout:', err);
    }

    return () => { if (unsub) unsub(); };
  }, [activeEntity?.id]);

  const handleCreatePost = async () => {
    if (!newPost.trim() || !activeEntity || !club || !user) return;
    setPostingPost(true);
    try {
      const displayName = user.displayName || user.email?.split('@')[0] || 'Lid';
      await addDoc(collection(db, 'networkClubNews'), {
        entityId: activeEntity.id,
        clubId: club.id,
        clubName: club.name,
        userId: user.uid,
        authorName: displayName,
        authorPhoto: user.photoURL || '',
        authorRole: activeEntity.role || 'member',
        content: newPost.trim(),
        createdAt: serverTimestamp(),
      });
      setNewPost('');
      void loadClubPosts(club.id);
    } catch (e) {
      console.error('Post mislukt:', e);
    } finally {
      setPostingPost(false);
    }
  };

  // "Delen" vanuit besloten feed  post naar publieke nieuwsfeed (communityPosts zonder entityId-beperking)
  const shareToPublicFeed = async (payload: any) => {
    if (!user?.uid || !club) return;
    const baseContent = String(payload?.content || payload?.text || payload?.message || '').trim();
    const note = String(payload?.shareNote || '').trim();
    const sharedFromClub = club.name || activeEntity?.name || '';
    const prefix = sharedFromClub ? `Gedeeld vanuit ${sharedFromClub}:\n\n` : '';
    const content = [note, `${prefix}${baseContent}`].filter(Boolean).join('\n\n').slice(0, 2000);
    if (!content) return;
    await addDoc(collection(db, 'communityPosts'), {
      userId: user.uid,
      author: user.displayName || user.email || 'Lid',
      authorPhoto: user.photoURL || '',
      content,
      timestamp: serverTimestamp(),
      // Geen entityId  zichtbaar in publieke nieuwsfeed
      visibility: 'public',
      source: 'group_feed_share',
      sharedFromEntityId: activeEntity?.id || null,
      sharedFromClubName: sharedFromClub || null,
      originalPostId: payload?.id || null,
    });
    setShareToast('Gedeeld naar de publieke nieuwsfeed! Zichtbaar op het dashboard — Feeds.');
    setTimeout(() => setShareToast(null), 4000);
  };

  // (startOnlineMeeting is defined above with activeMeetings persistence)

  const memberCount = members.length;
  const memberById = useMemo(() => {
    const map = new Map<string, ClubInviteMemberRow>();
    members.forEach((m) => map.set(String(m.id), m));
    return map;
  }, [members]);
  const minMembers = club?.minMembersRequired || 5;
  const isActive = club?.isActive === true;
  const isOwnerOrOrganizer = !isApprovedGuest && (activeEntity?.role === 'owner' || activeEntity?.role === 'organizer');
  // Bestuur (ledenraad) heeft dezelfde post/management rechten als organizer, maar geen club-instellingen
  const isBoard = !isApprovedGuest && activeEntity?.role === 'board';
  // Kan opnemen en rapport genereren: owner, organizer, admin én board
  const isHostInMeeting = isOwnerOrOrganizer || isBoard;
  // Leden die posts mogen plaatsen in de besloten feed
  const canPostInFeed = !isApprovedGuest; // alle leden (ook gewone leden), maar geen gasten

  const hubCaps = useMemo(
    () => resolveHubCapabilities(activeEntity?.role, activeEntity?.capabilities ?? undefined),
    [activeEntity?.role, activeEntity?.capabilities],
  );
  const isOrganizerRoleHub = activeEntity?.role === 'organizer';
  const isOwnerRoleHub = activeEntity?.role === 'owner';
  const isBoardRoleHub = activeEntity?.role === 'board';
  const canInviteMembersHubModal =
    !isApprovedGuest && (isOwnerRoleHub || isBoardRoleHub || (isOrganizerRoleHub && hubCaps.canInviteMembers));
  const canManageAgendaHub =
    !isApprovedGuest && (isOwnerRoleHub || isBoardRoleHub || (isOrganizerRoleHub && hubCaps.canManageAgenda));

  const copyOnboardingInviteLink = useCallback(async () => {
    if (!activeEntity?.id || !user?.uid) return;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!origin) return;
    const link = `${origin}/?onboarding=1&inviteEntity=${activeEntity.id}&inviteBy=${user.uid}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      /* ignore */
    }
  }, [activeEntity?.id, user?.uid]);

  const progressPct = Math.min(100, Math.round((memberCount / minMembers) * 100));
  const completedVisits = useMemo(
    () =>
      visitRequests.filter((req: any) => {
        if (req?.status !== 'accepted') return false;
        const visitDate = req?.visitDate instanceof Date ? req.visitDate : new Date(req?.visitDate || Date.now());
        return Number.isFinite(visitDate.getTime()) && visitDate.getTime() <= Date.now();
      }),
    [visitRequests]
  );
  const visitorMonthOptions = useMemo(() => {
    const monthSet = new Set(
      completedVisits.map((req: any) => {
        const visitDate = req?.visitDate instanceof Date ? req.visitDate : new Date(req?.visitDate || Date.now());
        const y = visitDate.getFullYear();
        const m = `${visitDate.getMonth() + 1}`.padStart(2, '0');
        return `${y}-${m}`;
      })
    );
    return Array.from(monthSet).sort((a, b) => (a < b ? 1 : -1));
  }, [completedVisits]);
  const visitorsBySelectedMonth = useMemo(() => {
    if (selectedVisitorMonth === 'all') return completedVisits;
    return completedVisits.filter((req: any) => {
      const visitDate = req?.visitDate instanceof Date ? req.visitDate : new Date(req?.visitDate || Date.now());
      const key = `${visitDate.getFullYear()}-${`${visitDate.getMonth() + 1}`.padStart(2, '0')}`;
      return key === selectedVisitorMonth;
    });
  }, [completedVisits, selectedVisitorMonth]);
  const topVisitorFields = useMemo(() => {
    const counts = new Map<string, number>();
    visitorsBySelectedMonth.forEach((req: any) => {
      const expertise = Array.isArray(req?.visitorExpertise) ? req.visitorExpertise : [];
      expertise.forEach((field: string) => counts.set(field, (counts.get(field) || 0) + 1));
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [visitorsBySelectedMonth]);
  const uniqueVisitorCountByMonth = useMemo(
    () => new Set(visitorsBySelectedMonth.map((req: any) => req.visitorId).filter(Boolean)).size,
    [visitorsBySelectedMonth]
  );

  useEffect(() => {
    if (selectedVisitorMonth === 'all') return;
    if (!visitorMonthOptions.includes(selectedVisitorMonth)) {
      setSelectedVisitorMonth(visitorMonthOptions[0] || 'all');
    }
  }, [selectedVisitorMonth, visitorMonthOptions]);
  const agendaMonthOptions = useMemo(() => {
    const keys = new Set(
      clubEvents.map((ev: any) => {
        const d = ev?.eventDate instanceof Date ? ev.eventDate : new Date();
        return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}`;
      })
    );
    return Array.from(keys).sort((a, b) => (a < b ? 1 : -1));
  }, [clubEvents]);
  const filteredAgendaEvents = useMemo(() => {
    if (selectedAgendaMonth === 'all') return clubEvents;
    return clubEvents.filter((ev: any) => {
      const d = ev?.eventDate instanceof Date ? ev.eventDate : new Date();
      const key = `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}`;
      return key === selectedAgendaMonth;
    });
  }, [clubEvents, selectedAgendaMonth]);
  const now = Date.now();
  const upcomingAgendaEvents = filteredAgendaEvents.filter((ev: any) => (ev?.eventDate?.getTime?.() || 0) >= now);
  const pastAgendaEvents = filteredAgendaEvents.filter((ev: any) => (ev?.eventDate?.getTime?.() || 0) < now);
  const nextMeetingEvent = useMemo(() => {
    const toEventStartMs = (ev: any) => {
      const d = ev?.eventDate instanceof Date ? ev.eventDate : new Date();
      if (!(d instanceof Date) || Number.isNaN(d.getTime())) return 0;
      if (typeof ev?.startTime === 'string' && /^\d{2}:\d{2}$/.test(ev.startTime)) {
        const [hh, mm] = ev.startTime.split(':').map(Number);
        const withTime = new Date(d);
        withTime.setHours(hh, mm, 0, 0);
        return withTime.getTime();
      }
      return d.getTime();
    };
    const sorted = [...clubEvents].sort((a: any, b: any) => toEventStartMs(a) - toEventStartMs(b));
    return sorted.find((ev: any) => toEventStartMs(ev) >= Date.now()) || null;
  }, [clubEvents]);

  useEffect(() => {
    const loadCurrentUserRsvp = async () => {
      if (!user?.uid || !nextMeetingEvent?.id) {
        setNextMeetingRsvp(null);
        setNextMeetingReason('');
        setShowDeclineReason(false);
        return;
      }
      try {
        const rsvpRef = doc(db, 'clubEventRsvps', `${nextMeetingEvent.id}_${user.uid}`);
        const snap = await getDoc(rsvpRef);
        if (!snap.exists()) {
          setNextMeetingRsvp(null);
          setNextMeetingReason('');
          setShowDeclineReason(false);
          return;
        }
        const data: any = snap.data();
        const response = data?.response === 'yes' || data?.response === 'no' ? data.response : null;
        setNextMeetingRsvp(response);
        setNextMeetingReason(typeof data?.reason === 'string' ? data.reason : '');
        setShowDeclineReason(false);
      } catch {
        setNextMeetingRsvp(null);
        setNextMeetingReason('');
        setShowDeclineReason(false);
      }
    };
    void loadCurrentUserRsvp();
  }, [nextMeetingEvent?.id, user?.uid]);

  const saveNextMeetingRsvp = useCallback(
    async (response: 'yes' | 'no') => {
      if (!user?.uid || !nextMeetingEvent?.id) return;
      if (response === 'no' && !nextMeetingReason.trim()) return;
      setSavingNextMeetingRsvp(true);
      try {
        const rsvpRef = doc(db, 'clubEventRsvps', `${nextMeetingEvent.id}_${user.uid}`);
        await setDoc(
          rsvpRef,
          {
            eventId: nextMeetingEvent.id,
            entityId: activeEntity?.id || null,
            clubId: club?.id || null,
            userId: user.uid,
            userName: user.displayName || user.email || 'Lid',
            response,
            reason: response === 'no' ? nextMeetingReason.trim() : null,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setNextMeetingRsvp(response);
        if (response === 'yes') {
          setNextMeetingReason('');
          setShowDeclineReason(false);
        }
      } finally {
        setSavingNextMeetingRsvp(false);
      }
    },
    [activeEntity?.id, club?.id, nextMeetingEvent?.id, nextMeetingReason, user?.displayName, user?.email, user?.uid]
  );
  useEffect(() => {
    if (selectedAgendaMonth === 'all') return;
    if (!agendaMonthOptions.includes(selectedAgendaMonth)) {
      setSelectedAgendaMonth(agendaMonthOptions[0] || 'all');
    }
  }, [selectedAgendaMonth, agendaMonthOptions]);

  // Network page landing: open on "Ons verhaal" when switching entities.
  useEffect(() => {
    setActiveTab('mijnverhaal');
  }, [activeEntity?.id]);

  const clubWebsiteUrl = useMemo(
    () => normalizeNetworkClubWebsiteUrl(club?.website || ''),
    [club?.website]
  );

  useEffect(() => {
    if (!normalizeNetworkClubWebsiteUrl(club?.website || '')) {
      if (clubLandingView === 'website') setClubLandingView('overview');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club?.id, club?.website]);

  //  Tab config 
  const tabs = [
    { key: 'overview',     label: 'Overzicht',        icon: Building2 },
    { key: 'members',      label: `Leden (${memberCount})`, icon: Users },
    { key: 'guests',       label: `Gasten & Aanvragen (${guests.length + visitRequests.length})`, icon: User },
    { key: 'agenda',       label: `Agenda (${clubEvents.length})`, icon: Calendar },
    { key: 'videos',       label: "Video's",           icon: Video },
    ...(isOwnerOrOrganizer ? [
      { key: 'bijeenkomst', label: 'Bijeenkomst',      icon: Monitor },
      { key: 'invite',      label: 'Uitnodigen',       icon: UserPlus },
      { key: 'settings',    label: 'Instellingen',     icon: Settings },
    ] : []),
  ] as const;

  if (accessCheckLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Icon icon={Loader} size={28} strokeWidth={1.5} className="animate-spin text-orange-500" />
      </div>
    );
  }

  if (activeEntity && !hasClubAccess) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center py-24">
        <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4">
          <Icon icon={Users} size={28} strokeWidth={1.5} color="#f97316" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Toegang beperkt</h3>
        <p className="text-gray-500 text-sm">
          Alleen leden en toegelaten gasten door organisator/paginamanager kunnen deze pagina openen.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Icon icon={Loader} size={28} strokeWidth={1.5} className="animate-spin text-orange-500" />
      </div>
    );
  }

  if (!club) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center py-24">
        <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4">
          <Icon icon={Building2} size={28} strokeWidth={1.5} color="#f97316" />
          </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Geen club gevonden</h3>
        <p className="text-gray-500 text-sm">Selecteer een netwerkclub via de entiteitskiezer.</p>
        <div className="mt-6 max-w-xs mx-auto"><EntitySwitcher /></div>
      </div>
    );
  }

  //  Derived theme colours 
  const C = {
    primary:   (club as any).primaryColor   || '#2D5E4F',
    secondary: (club as any).secondaryColor || '#1B3A4B',
    accent:    (club as any).accentColor    || '#7A9E8E',   // sage lichtgroen
  };

  const typeLabel = club.type === 'networkclub' ? 'Netwerkclub'
    : club.type === 'mastermind' ? 'Mastermind' : 'Community';

  const TABS_CONFIG = [
    { key: 'mijnverhaal', label: 'Ons verhaal' },
    { key: 'overview',  label: 'Overzicht' },
    { key: 'mijnpitch', label: 'Mijn pitch' },
    { key: 'members',   label: `Leden (${memberCount})` },
    { key: 'guests',    label: `Gasten & Aanvragen (${guests.length + visitRequests.length})` },
    { key: 'agenda',    label: `Agenda (${clubEvents.length})` },
    { key: 'videos',    label: "Video's" },
    ...(isOwnerOrOrganizer ? [
      { key: 'invite',    label: 'Uitnodigen' },
      { key: 'settings',  label: 'Instellingen' },
    ] : []),
  ];

  return (
    <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#F0F4F8', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
      <style>{`
        @keyframes nc-fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        .nc-anim { animation: nc-fadeUp 0.3s ease; }
      `}</style>

      {/* Hidden cover input */}
      <input ref={coverInputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCoverUpload(f); if (coverInputRef.current) coverInputRef.current.value = ''; }} />
      <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); if (logoInputRef.current) logoInputRef.current.value = ''; }} />



      {/* ── COMPACTE HERO HEADER (horizontaal, ~110px) ──────────────────── */}
      <div
        ref={coverFrameRef}
        className={`relative flex-shrink-0 ${isOwnerOrOrganizer && coverImageUrl && isCoverEditMode ? 'cursor-move' : ''}`}
        style={{ background: `linear-gradient(135deg, ${C.secondary} 0%, ${C.primary} 55%, ${withAlpha(C.accent, 0.75)} 100%)` }}
        onMouseDown={(e) => {
          if (!isOwnerOrOrganizer || !coverImageUrl || !isCoverEditMode) return;
          setIsCoverDragging(true);
          updateCoverPositionFromEvent(e.clientX, e.clientY);
        }}
      >
        {/* Cover foto overlay */}
        {coverImageUrl && (
          <img src={coverImageUrl} alt="" draggable={false}
            className="absolute inset-0 w-full h-full object-cover select-none"
            style={{ opacity: 0.22, objectPosition: `${coverPosition.x}% ${coverPosition.y}%` }} />
        )}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: `radial-gradient(#fff 1px, transparent 1px)`, backgroundSize: '28px 28px' }} />

        {/* LIVE badge */}
        {activeMeeting && !showMeeting && (
          <div className="absolute top-2 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ backgroundColor: 'rgba(4,120,87,0.88)', backdropFilter: 'blur(8px)', border: '1px solid rgba(52,211,153,0.4)' }}>
            <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-300" /></span>
            <span className="text-[10px] font-bold text-emerald-100 tracking-widest">LIVE</span>
            </div>
        )}

        {/* Cover edit knoppen */}
        {isOwnerOrOrganizer && (
          <div className="absolute top-2 right-2 z-10 flex gap-1.5">
            {coverImageUrl && (
              <button onClick={() => setIsCoverEditMode(v => !v)} className="h-7 px-2.5 text-[11px] font-medium rounded-lg"
                style={{ background: 'rgba(0,0,0,0.5)', color: '#fff', backdropFilter: 'blur(6px)' }}>
                {isCoverEditMode ? '✓ Klaar' : '↔ Positie'}
              </button>
            )}
            <button onClick={() => coverInputRef.current?.click()} disabled={savingCover}
              className="h-7 px-2.5 text-[11px] font-medium rounded-lg flex items-center gap-1"
              style={{ background: 'rgba(0,0,0,0.5)', color: '#fff', backdropFilter: 'blur(6px)' }}>
              <Camera size={11} />{savingCover ? 'Uploaden\u2026' : 'Cover'}
            </button>
          </div>
        )}
        {isCoverEditMode && coverImageUrl && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="px-3 py-1.5 rounded-lg text-xs text-white" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }}>Sleep om foto te verschuiven</div>
          </div>
        )}

        {/* Horizontale identiteitsbalk */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '10px 20px 0', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Logo */}
          <div
            style={{ position: 'relative', width: 52, height: 52, borderRadius: 14, flexShrink: 0, background: withAlpha('#ffffff', 0.15), border: `2px solid ${withAlpha('#ffffff', 0.3)}`, backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, cursor: isOwnerOrOrganizer ? 'pointer' : 'default', overflow: 'hidden' }}
            onClick={() => isOwnerOrOrganizer && logoInputRef.current?.click()}
            title={isOwnerOrOrganizer ? 'Klik om logo te wijzigen' : undefined}
          >
            {club.logoUrl ? <img src={club.logoUrl} alt={club.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} /> : club.name.charAt(0)}
            {isOwnerOrOrganizer && (
              <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0.4)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,0)'}>
                {savingLogo ? <div style={{ width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%' }} /> : <Camera size={13} color="#fff" />}
              </div>
            )}
          </div>

          {/* Naam + badges */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1.2 }}>{club.name}</h1>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5, alignItems: 'center' }}>
              {/* Rol — eerste badge, duidelijk maar subtiel */}
              {activeEntity && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: withAlpha('#ffffff', 0.18), color: '#fff', borderRadius: 20, padding: '2px 9px', fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>
                  {activeEntity.role === 'owner' || activeEntity.role === 'organizer' ? 'Organisator' : activeEntity.role === 'board' ? 'Bestuur' : 'Lid'}
                    </span>
                  )}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: withAlpha('#ffffff', 0.10), color: withAlpha('#ffffff', 0.85), borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}><CompanyIcon size={10} weight="regular" />{typeLabel}</span>
              {club.region && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: withAlpha('#ffffff', 0.08), color: withAlpha('#ffffff', 0.8), borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}><MapPinIcon size={10} weight="regular" />{club.region}</span>}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: withAlpha(C.accent, 0.3), color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}><PhosphorUsers size={10} weight="regular" />{memberCount}/{minMembers}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: withAlpha('#ffffff', 0.08), color: withAlpha('#ffffff', 0.75), borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>
                {club.visibility === 'public' ? <><GlobeIcon size={10} weight="regular" />Open</> : <><LockIcon size={10} weight="regular" />Besloten</>}
                    </span>
            </div>
          </div>

          {/* Acties rechts */}
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
            {!showMeeting ? (
              isOwnerOrOrganizer ? (
                <>
                  <button onClick={startOnlineMeeting}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.accent, color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: `0 3px 10px ${withAlpha(C.accent, 0.45)}` }}>
                    <MicrophoneIcon size={13} weight="regular" />Start online meeting
                  </button>
                  <button onClick={() => setPhysicalSetupOpen(true)}
                    title="PDF-upload en start fysieke bijeenkomst"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: withAlpha('#ffffff', 0.12), color: '#fff', border: `1.5px solid ${withAlpha('#ffffff', 0.28)}`, backdropFilter: 'blur(4px)', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    <ProjectorScreenIcon size={13} weight="regular" />Start fysieke meeting
                  </button>
                </>
              ) : (
                <button onClick={() => setShowRequestVisitModal(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.accent, color: '#fff', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: `0 3px 10px ${withAlpha(C.accent, 0.45)}` }}>
                  <MicrophoneIcon size={14} weight="regular" />Club bezoeken
                </button>
              )
            ) : (
              <button onClick={handleLeaveMeeting}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#EF4444', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                <EndCallIcon size={14} weight="regular" />Verlaten
              </button>
            )}
          </div>
        </div>

        {/* Compacte stat-strip onderaan header */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '8px 20px', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0 }}>
          {[['Leden', memberCount], ['Gasten', guests.length], ['Bezoekers', visitRequests.length], ['Agenda', clubEvents.length]].map(([label, value], i) => (
            <div key={String(label)} style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 16, marginRight: 16, borderRight: i < 3 ? `1px solid ${withAlpha('#ffffff', 0.18)}` : 'none' }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{String(value)}</span>
              <span style={{ fontSize: 10.5, color: withAlpha('#ffffff', 0.6), fontWeight: 600 }}>{String(label)}</span>
            </div>
          ))}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 4, background: withAlpha('#ffffff', 0.2), borderRadius: 10, overflow: 'hidden', maxWidth: 120 }}>
              <div style={{ width: `${progressPct}%`, height: '100%', background: '#fff', borderRadius: 10, transition: 'width 0.6s ease', opacity: 0.9 }} />
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: withAlpha('#ffffff', 0.75) }}>{progressPct}% actief</span>
          </div>
        </div>
      </div>

      {/*  TAB NAVIGATION  */}
      <div style={{ background: '#fff', borderBottom: '1px solid #F3F4F6', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', overflowX: 'auto', padding: '0 8px' }}>
          {TABS_CONFIG.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as any)}
              style={{
                padding: '14px 16px', fontSize: 12.5, fontWeight: 600,
                border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
                color: activeTab === tab.key ? C.primary : '#9CA3AF',
                borderBottom: activeTab === tab.key ? `2.5px solid ${C.primary}` : '2.5px solid transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {physicalSetupOpen && activeEntity && !showMeeting && (
        <PhysicalPresentationSetup
          entityId={activeEntity.id}
          clubName={club?.name || 'Club'}
          accentColor={C.accent}
          primaryColor={C.primary}
          onClose={() => setPhysicalSetupOpen(false)}
          onStartPresentation={() => {
            setPhysicalSetupOpen(false);
            setShowPhysicalCountdown(true);
            // Start de meeting direct op de achtergrond zodat alles laadt
            // terwijl de countdown zichtbaar is.
            void startPhysicalMeeting();
          }}
        />
      )}

      {/* PRE-MEETING COUNTDOWN — fysieke modus, laadt meeting op achtergrond */}
      {showPhysicalCountdown && activeEntity && (
        <MeetingCountdown
          entityId={activeEntity.id}
          clubName={club?.name}
          logoUrl={(club as any)?.logoUrl || undefined}
          musicUrl={null}
          videoUrl={null}
          imageUrl={null}
          primaryColor={C.primary}
          isHost={true}
          startLabel={"Start bijeenkomst \u2192"}
          onMeetingStart={() => setShowPhysicalCountdown(false)}
          onClose={() => {
            setShowPhysicalCountdown(false);
            // Annuleren: meeting ook afsluiten als die al gestart was
            void handleLeaveMeeting();
          }}
        />
      )}

      {/*  INLINE MEETING (fullscreen overlay blijft buiten de scroll-container) */}
      {showMeeting && meetingRoom && activeEntity && (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <NetworkClubMeeting
            roomName={meetingRoom}
            meetingTitle={`${club?.name} - ${meetingIsPhysical ? 'Fysieke Bijeenkomst' : 'Online Meeting'}`}
            clubName={club?.name}
            entityId={activeEntity.id}
            entityType={activeEntity.type}
            clubId={club?.id}
            isHost={isHostInMeeting}
            autoStartCountdown={isHostInMeeting && !meetingIsPhysical}
            autoOpenPresentation={autoOpenPresentation}
            initialPhysical={meetingIsPhysical}
            clubLogoUrl={(club as any)?.logoUrl || undefined}
            overlaySettings={(club as any)?.presentationOverlaySettings || {}}
            presentationColors={{
              primary: C.primary,
              secondary: C.secondary,
              accent: C.accent,
            }}
            memberNames={members.map((m) => m.name)}
            memberCount={members.length}
            memberAvatars={members.map((m) => m.photoURL ?? null)}
            onLeave={handleLeaveMeeting}
          />
                </div>
      )}

      {/*  MAIN CONTENT — flex-1 zodat het de rest van 100vh vult, intern scrollbaar */}
      {!showMeeting && (
      <div className="nc-anim" style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* ── TERUGKNOP — alleen zichtbaar als een subview open is ── */}
        {activeTab === 'mijnverhaal' && clubLandingView !== 'overview' && (
          <div style={{ flexShrink: 0, padding: '8px 14px 0' }}>
            <button type="button" onClick={() => setClubLandingView('overview')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${withAlpha(C.primary, 0.2)}`, background: '#fff', color: C.primary, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              ← Terug
            </button>
          </div>
        )}

        {/* ── ONS VERHAAL VIEW — fullscreen, identieke layout-logica als website view ── */}
        {activeTab === 'mijnverhaal' && clubLandingView === 'story' && (
          <div style={{ flex: 1, minHeight: 0, padding: '8px 14px 12px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, background: '#F9FAFB', borderRadius: 10, border: `1px solid ${withAlpha(C.primary, 0.12)}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Nav bar — identiek aan website-view nav bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '8px 14px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA', flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: '#6B7280', minWidth: 0 }}>
                  <span style={{ fontWeight: 700, color: '#374151' }}>Ons verhaal van {club.name}</span>
                  <span style={{ display: 'block', marginTop: 1, fontSize: 11 }}>{typeLabel} · SuperKonnected</span>
                </div>
                {isOwnerOrOrganizer && (
                  <button type="button" onClick={() => setActiveTab('settings' as any)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${withAlpha(C.primary, 0.25)}`, background: '#fff', color: C.primary, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                    ✎ Verhaal bewerken
                  </button>
                )}
              </div>

              {/* Scrollable story content */}
              <div style={{ flex: 1, overflowY: 'auto' }}>

                {/* HERO */}
                <div style={{ position: 'relative', minHeight: 240, background: `linear-gradient(135deg, ${withAlpha(C.secondary, 0.95)} 0%, ${withAlpha(C.primary, 0.95)} 55%, ${withAlpha(C.accent, 0.9)} 100%)`, overflow: 'hidden' }}>
                  {coverImageUrl && (
                    <img src={coverImageUrl} alt="" aria-hidden="true"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${coverPosition.x}% ${coverPosition.y}%`, opacity: 0.38 }} />
                  )}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)' }} />
                  <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '36px 20px 60px' }}>
                    {/* Club logo */}
                    <div style={{ width: 80, height: 80, borderRadius: '50%', border: '4px solid rgba(255,255,255,0.9)', background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', overflow: 'hidden', marginBottom: 16 }}>
                      {(club as any).logoUrl ? (
                        <img src={(club as any).logoUrl} alt={`Logo ${club.name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${C.primary}, ${C.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 28, fontWeight: 900, color: '#fff' }}>{club.name.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                    {/* h1 — GEO/SEO visible text */}
                    <h1 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 8px', textAlign: 'center', lineHeight: 1.15, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                      {club.name}
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 18 }}>
                      <span style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700, border: '1px solid rgba(255,255,255,0.25)' }}>{typeLabel}</span>
                      {(club as any).categories?.slice(0, 2).map((cat: string, i: number) => (
                        <span key={i} style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', borderRadius: 20, padding: '3px 9px', fontSize: 11.5, fontWeight: 600 }}>{cat}</span>
                      ))}
                      {((club as any).city || (club as any).region) && (
                        <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                          📍 {[(club as any).city, (club as any).region].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </div>
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {clubWebsiteUrl && (
                        <button type="button" onClick={() => void openExternalUrl(clubWebsiteUrl)}
                          style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: '#fff', color: C.primary, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                          🌐 Website
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* WHITE CONTENT SECTION */}
                <div style={{ maxWidth: 740, margin: '0 auto', padding: '28px 20px 48px', display: 'flex', flexDirection: 'column', gap: 22 }}>
                  {/* ONS VERHAAL + WAARDEN */}
                  {(() => {
                    const clubDesc: string = (club as any).description || '';
                    const clubVals: string[] = Array.isArray((club as any).values) ? (club as any).values
                      : Array.isArray((club as any).categories) ? (club as any).categories.slice(0, 3) : [];
                    return (
                      <section>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                          <div style={{ width: 4, height: 24, borderRadius: 4, background: `linear-gradient(to bottom, ${C.primary}, ${C.accent})` }} />
                          <h2 style={{ fontSize: 18, fontWeight: 900, color: '#111827', margin: 0 }}>Ons verhaal</h2>
                        </div>
                        {/* Waarden cards */}
                        {clubVals.length > 0 && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 18 }}>
                            {clubVals.slice(0, 3).map((val: string, i: number) => (
                              <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: `1px solid ${withAlpha(C.primary, 0.12)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>Waarde {i + 1}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{val}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Beschrijving */}
                        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', border: `1px solid ${withAlpha(C.primary, 0.08)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                          {clubDesc ? (
                            <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>{clubDesc}</p>
                          ) : (
                            <p style={{ fontSize: 13.5, color: '#9CA3AF', fontStyle: 'italic', margin: 0, lineHeight: 1.7 }}>
                              {isOwnerOrOrganizer
                                ? 'Voeg jullie verhaal toe via Instellingen → Clubverhaal.'
                                : 'Dit club heeft nog geen verhaal toegevoegd.'}
                            </p>
                          )}
                        </div>
                      </section>
                    );
                  })()}

                  {/* ONTMOET DE FOUNDER */}
                  {(() => {
                    const founder = members.find(m => m.role === 'owner') || members.find(m => m.role === 'organizer');
                    if (!founder) return null;
                    return (
                      <section>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                          <div style={{ width: 4, height: 24, borderRadius: 4, background: `linear-gradient(to bottom, ${C.primary}, ${C.accent})` }} />
                          <h2 style={{ fontSize: 18, fontWeight: 900, color: '#111827', margin: 0 }}>Ontmoet de Founder</h2>
                        </div>
                        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', border: `1px solid ${withAlpha(C.primary, 0.08)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                          <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: `3px solid ${withAlpha(C.primary, 0.15)}`, background: '#F3F4F6' }}>
                            {founder.photoURL ? (
                              <img src={founder.photoURL} alt={`Foto van ${founder.name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: withAlpha(C.primary, 0.1), color: C.primary, fontSize: 22, fontWeight: 800 }}>
                                {founder.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 16, fontWeight: 800, color: '#111827', margin: '0 0 2px' }}>{founder.name}</p>
                            {(founder as any).functionTitle && (
                              <p style={{ fontSize: 12.5, color: '#6B7280', margin: '0 0 10px' }}>{(founder as any).functionTitle}</p>
                            )}
                            <button type="button"
                              onClick={() => {
                                window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId: founder.id } }));
                                window.location.hash = '#mijn-verhaal';
                              }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: `1.5px solid ${withAlpha(C.primary, 0.3)}`, background: 'transparent', color: C.primary, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                              Bekijk persoonlijk verhaal →
                            </button>
                          </div>
                        </div>
                      </section>
                    );
                  })()}
                </div>
              </div>

              {/* Footer hint */}
              <div style={{ padding: '6px 14px', fontSize: 10.5, color: '#9CA3AF', borderTop: '1px solid #F3F4F6', flexShrink: 0, background: '#FAFAFA' }}>
                Slug: superkonnected.com/clubs/{generateClubSlug(club.name)}
              </div>
            </div>
          </div>
        )}

        {/* ── WEBSITE VIEW — volledige breedte, buiten het 3-koloms grid ── */}
        {activeTab === 'mijnverhaal' && clubLandingView === 'website' && clubWebsiteUrl && (
          <div style={{ flex: 1, minHeight: 0, padding: '8px 14px 12px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, background: '#fff', borderRadius: 10, border: `1px solid ${withAlpha(C.primary, 0.12)}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Adresbalk */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '8px 14px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA', flexShrink: 0 }}>
                <div style={{ fontSize: 11.5, color: '#6B7280', minWidth: 0 }}>
                  <span style={{ fontWeight: 700, color: '#374151' }}>Website van {club.name}</span>
                  <span style={{ display: 'block', marginTop: 1, wordBreak: 'break-all' }}>{clubWebsiteUrl}</span>
                </div>
                <button type="button" onClick={() => void openExternalUrl(clubWebsiteUrl)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${withAlpha(C.primary, 0.25)}`, background: '#fff', color: C.primary, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                  <LaunchIcon size={14} weight="regular" />
                  {Capacitor.isNativePlatform() ? 'Open in app-browser' : 'Open in nieuw tabblad'}
                </button>
              </div>
              {/* Iframe of native-bericht */}
              {Capacitor.isNativePlatform() ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, textAlign: 'center', padding: '24px 18px', background: '#F9FAFB' }}>
                  <p style={{ fontSize: 13, color: '#6B7280', margin: 0, maxWidth: 360, lineHeight: 1.5 }}>
                    Op mobiel openen we externe websites in de ingebouwde browser. Tik op de knop hierboven.
                  </p>
                  <button type="button" onClick={() => void openExternalUrl(clubWebsiteUrl)}
                    style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: C.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Website openen
                  </button>
                </div>
              ) : (
                <iframe title={`Website ${club.name}`} src={clubWebsiteUrl}
                  style={{ flex: 1, width: '100%', border: 'none', display: 'block', minHeight: 0 }}
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
                  referrerPolicy="no-referrer-when-downgrade" />
              )}
              <div style={{ padding: '6px 14px', fontSize: 10.5, color: '#9CA3AF', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
                {Capacitor.isNativePlatform() ? 'Sluit de browser om terug te keren.' : 'Sommige sites blokkeren insluiting. Gebruik dan "Open in nieuw tabblad".'}
              </div>
            </div>
          </div>
        )}

        {/* ── 3-KOLOMS GRID — verborgen bij story- en website-view ── */}
        {!(activeTab === 'mijnverhaal' && (clubLandingView === 'story' || (clubLandingView === 'website' && clubWebsiteUrl))) && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: '200px 1fr 280px', gap: 12, padding: '12px 14px', maxWidth: 1200, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

        {/* ── ONLINE SIDEBAR (links) ─────────────────────────────────── */}
        <div style={{ minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, paddingBottom: 4, borderBottom: '1px solid #F3F4F6' }}>
            Online nu ({onlinePresences.length})
          </div>
          {onlinePresences.length === 0 ? (
            <div style={{ fontSize: 11.5, color: '#D1D5DB', textAlign: 'center', paddingTop: 16 }}>Niemand online</div>
          ) : (
            onlinePresences.map(p => (
              <OnlineMemberRow key={p.userId} presence={p} currentUserId={user?.uid || ''} accentColor={C.accent} />
            ))
              )}
            </div>
            
        {/*  CENTER: Tab content — interne scroll */}
        <div className="scrollbar-always" style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: 6, scrollbarGutter: 'stable', display: 'flex', flexDirection: 'column' }}>

          {/* ONS VERHAAL — standaard overzicht in het grid */}
          {activeTab === 'mijnverhaal' && clubLandingView !== 'website' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(() => {
                // Oprichter = eerste owner/organizer in de ledenlijst
                const founder = members.find(m => m.role === 'owner') || members.find(m => m.role === 'organizer');
                const clubDescription = (club as any).description as string | undefined;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Hero kaart */}
                    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.12)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                      <div style={{ position: 'relative', height: 210, background: `linear-gradient(135deg, ${withAlpha(C.secondary, 0.92)} 0%, ${withAlpha(C.primary, 0.92)} 58%, ${withAlpha(C.accent, 0.85)} 100%)` }}>
                        {coverImageUrl && (
                          <img
                            src={coverImageUrl}
                            alt="Omslag afbeelding"
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ objectPosition: `${coverPosition.x}% ${coverPosition.y}%`, opacity: 0.35 }}
                          />
                        )}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0.1))' }} />
                        <div style={{ position: 'absolute', left: 18, bottom: 16, right: 18, color: '#fff' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>{club.name}</div>
                          <div style={{ marginTop: 6, fontSize: 13, color: withAlpha('#ffffff', 0.9) }}>
                            {typeLabel} · SuperKonnected
                          </div>
                        </div>
                      </div>

                      {/* Clubverhaal / filosofie */}
                      <div style={{ padding: '16px 18px' }}>
                        {clubDescription ? (
                          <p style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7, margin: 0 }}>{clubDescription}</p>
                        ) : (
                          <p style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                            {isOwnerOrOrganizer
                              ? 'Voeg het verhaal en de filosofie van jullie club toe via Instellingen → Clubverhaal. Leden zien hier dan wie jullie zijn en waar jullie voor staan.'
                              : 'Nog geen clubverhaal toegevoegd.'}
                          </p>
                        )}
                      </div>

                      {/* Knoppen: Ons verhaal · Onze website · Verhaal van de Founder */}
                      <div style={{ padding: '0 18px 18px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setClubLandingView('story')}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: 'none', background: C.primary, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Ons verhaal
                        </button>
                        {clubWebsiteUrl && (
                          <button
                            type="button"
                            onClick={() => setClubLandingView('website')}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${withAlpha(C.primary, 0.3)}`, background: 'transparent', color: C.primary, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                          >
                            🌐 Onze website
                          </button>
                        )}
                        {founder && (
                          <button
                            type="button"
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId: founder.id } }));
                              window.location.hash = '#mijn-verhaal';
                            }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${withAlpha(C.primary, 0.3)}`, background: 'transparent', color: C.primary, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                          >
                            {founder.photoURL && (
                              <img src={founder.photoURL} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            )}
                            Verhaal van de Founder
                          </button>
                        )}
                      </div>

                    </div>

                    {/* Hint voor organizers: website koppelen */}
                    {isOwnerOrOrganizer && !clubWebsiteUrl && (
                      <div style={{ background: withAlpha(C.primary, 0.05), borderRadius: 12, border: `1px solid ${withAlpha(C.primary, 0.12)}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: '#6B7280', lineHeight: 1.5 }}>
                          <strong style={{ color: C.primary }}>Tip:</strong> Koppel jullie website via <strong>Instellingen → Website van de club</strong> — leden kunnen dan schakelen tussen ons verhaal en de clubsite.
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveTab('settings' as any)}
                          style={{ padding: '7px 14px', borderRadius: 9, border: 'none', background: C.primary, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                        >
                          Ga naar Instellingen
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* OVERZICHT */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Live meeting kaart — zichtbaar voor alle leden wanneer een online meeting actief is */}
              {activeMeeting && !showMeeting && (
                <LiveMeetingCard
                  meeting={activeMeeting}
                  colors={C}
                  onJoin={() => { setMeetingRoom(activeMeeting.roomName); setAutoOpenPresentation(true); setShowMeeting(true); }}
                />
              )}

              {/* Post creation — owner, organizer én bestuur/ledenraad */}
              {canPostInFeed && (
                <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.1)}`, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg,${C.primary},${C.secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                      {(user?.displayName || user?.email || '?')[0].toUpperCase()}
            </div>
                    <textarea
                      value={newPost}
                      onChange={e => setNewPost(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && newPost.trim()) { e.preventDefault(); void handleCreatePost(); }}}
                      placeholder={`Deel een update met ${club.name}\u2026`}
                      rows={newPost ? 3 : 1}
                      style={{ flex: 1, background: '#F9FAFB', borderRadius: 8, padding: '9px 13px', fontSize: 13, color: newPost ? '#111827' : '#9CA3AF', border: '1px solid #E5E7EB', resize: 'none', outline: 'none' }}
                    />
            </div>
                  {newPost.trim() && (
                    <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#FAFAFA' }}>
                      <button onClick={() => setNewPost('')} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', fontSize: 12, color: '#6B7280', cursor: 'pointer', fontWeight: 600 }}>Annuleren</button>
                      <button onClick={() => void handleCreatePost()} disabled={postingPost} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: C.primary, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: postingPost ? 0.6 : 1 }}>
                        {postingPost ? 'Plaatsen\u2026' : 'Plaatsen'}
                      </button>
              </div>
            )}
                  <div style={{ padding: '8px 16px', background: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}></span>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>Besloten feed — alleen zichtbaar voor leden</span>
          </div>
        </div>
              )}
              {/* Feed posts */}
              {clubPosts.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: 14, border: `1.5px dashed ${withAlpha(C.primary, 0.2)}`, padding: '32px 24px', textAlign: 'center' }}>
                  <AnnouncementIcon size={32} weight="light" color={C.primary} style={{ marginBottom: 10, opacity: 0.4 }} />
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Nog geen posts</div>
                  <div style={{ fontSize: 12.5, color: '#9CA3AF', lineHeight: 1.6 }}>Deel een update, foto of aankondiging met je clubleden</div>
      </div>
              ) : (
                <div className="feed-container max-h-[50vh] overflow-y-auto overflow-x-hidden space-y-2.5 pr-1">
                  {clubPosts.map(p => (
                    <ClubPostCard
                      key={p.id}
                      post={p}
                      currentUser={user}
                      colors={C}
                      onShareToNewsfeed={shareToPublicFeed}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MIJN PITCH */}
          {activeTab === 'mijnpitch' && activeEntity && user && (
            <MijnPitchTab
              entityId={activeEntity.id}
              clubId={club?.id ?? ''}
              userId={user.uid}
              displayName={user.displayName || user.email?.split('@')[0] || 'Lid'}
              colors={C}
            />
          )}

          {/* LEDEN */}
          {activeTab === 'members' && (
            <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Leden ({memberCount})</div>
                {isOwnerOrOrganizer && (
                  <button onClick={() => setActiveTab('invite' as any)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, background: C.accent, color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    <InviteIcon size={14} weight="regular" />Uitnodigen
            </button>
                )}
              </div>
              {members.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF', fontSize: 13 }}>Nog geen leden</div>
              ) : (
                members.map(m => (
                  <MemberRow
                    key={m.id}
                    member={m}
                    canManage={isOwnerOrOrganizer}
                    colors={C}
                    entityId={activeEntity!.id}
                  />
                ))
              )}
            </div>
          )}

          {/* GASTEN & AANVRAGEN — samengevoegd */}
          {activeTab === 'guests' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Sectie 1: Uitgenodigde gasten */}
              <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Uitgenodigde gasten ({guests.length})</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>Mensen die jij actief hebt uitgenodigd voor de club</div>
                  </div>
                  {isOwnerOrOrganizer && (
                    <button onClick={() => setShowInviteGuestModal(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, background: C.accent, color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                      <InviteIcon size={14} weight="regular" />Gast uitnodigen
            </button>
                  )}
                </div>
                <div style={{ marginTop: 12 }}>
                  {guests.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#9CA3AF', fontSize: 13 }}>Nog geen gasten uitgenodigd</div>
                  ) : (
                    guests.map(g => (
                      <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#6B7280', flexShrink: 0 }}>
                          {(g.name || 'G')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{g.name}</div>
                          <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{g.email}</div>
                        </div>
                        <span style={{ background: g.status === 'confirmed' || g.status === 'attended' ? '#ECFDF5' : '#FFFBEB', color: g.status === 'confirmed' || g.status === 'attended' ? '#065F46' : '#92400E', border: `1.5px solid ${g.status === 'confirmed' || g.status === 'attended' ? '#A7F3D0' : '#FDE68A'}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                          {g.status === 'attended' ? 'Aanwezig geweest' : g.status === 'confirmed' ? 'Bevestigd' : 'Uitgenodigd'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Sectie 2: Bezoekverzoeken */}
              <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '16px 20px' }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Bezoekverzoeken ({visitRequests.length})</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>Mensen die zichzelf hebben aangemeld om de club te bezoeken</div>
                </div>
                {activeEntity && <VisitRequestManager clubId={club.id} entityId={activeEntity.id} accentColor={C.accent} />}
              </div>

            </div>
          )}

          {/* AGENDA — één systeem: ClubMeetingPlanner (calendarEvents, persoonlijke agenda, RSVP) */}
          {activeTab === 'agenda' && activeEntity && (
            <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '18px 20px' }}>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', display: 'inline-block', flexShrink: 0 }} />
                Verschijnt automatisch in de persoonlijke agenda van alle leden
              </div>
              <ClubMeetingPlanner
                entityId={activeEntity.id}
                clubName={club.name}
                currentUserId={user?.uid || ''}
                currentUserName={user?.displayName || user?.email?.split('@')[0] || 'Organisator'}
                memberUids={members.map(m => m.id)}
                canPlan={canManageAgendaHub}
                primaryColor={C.primary}
                accentColor={C.accent}
              />
            </div>
          )}

          {/* VIDEO'S */}
          {activeTab === 'videos' && activeEntity && (
            <ClubVideosSection
              clubId={club.id} entityId={activeEntity.id} clubName={club.name}
              canUpload={isOwnerOrOrganizer || activeEntity.role === 'board'}
            />
          )}

          {/* UITNODIGEN — zelfde flow als Mastermind (leden, capabilities, entityInvitations) */}
          {activeTab === 'invite' && activeEntity && club && user && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <ClubInviteMembersModal
                variant="inline"
                clubName={club.name}
                entityId={activeEntity.id}
                clubId={club.id}
                userId={user.uid}
                userName={user.displayName || user.email || 'Lid'}
                userRole={activeEntity.role}
                entityType={activeEntity.type}
                members={members}
                isOwner={isOwnerRoleHub}
                isBoard={isBoardRoleHub}
                canInviteMembersHub={canInviteMembersHubModal}
                accentColor={C.accent}
                inviteRoleExtras={activeEntity.type === 'networkclub' ? ['board', 'va'] : undefined}
                onMembersChanged={() => void loadClubData(activeEntity.id)}
              />

              <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '18px 20px' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 2 }}>Gast uitnodigen</div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF', marginBottom: 12 }}>Nodig iemand uit als gast voor een bijeenkomst (geen lidmaatschap vereist).</div>
                <button type="button" onClick={() => setShowInviteGuestModal(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 18px', borderRadius: 10, background: withAlpha(C.primary, 0.08), color: C.primary, border: `1.5px solid ${withAlpha(C.primary, 0.2)}`, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                  <InviteIcon size={14} weight="regular" />Gast uitnodigen
                </button>
              </div>
            </div>
          )}

          {/* BIJEENKOMST TIJDLIJN */}
          {activeTab === 'bijeenkomst' && activeEntity && (
            <MeetingTimelineEditor entityId={activeEntity.id} clubId={club.id} colors={C} />
          )}

          {/* INSTELLINGEN */}
          {activeTab === 'settings' && (
            <ClubSettingsSection club={club} onUpdate={() => loadClubData(activeEntity!.id)} />
          )}
              </div>

        {/*  RIGHT SIDEBAR — interne scroll */}
        <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', paddingBottom: 8 }}>

          {/* Volgende meeting RSVP */}
          {clubEvents.length > 0 && (
            <NextMeetingWidget event={clubEvents[0]} primary={C.primary} secondary={C.secondary} />
          )}

          {/* Daarna — tweede komende meeting, compact */}
          {clubEvents.length > 1 && (() => {
            const next = clubEvents[1];
            const d = next?.eventDate instanceof Date ? next.eventDate : new Date(next?.eventDate || Date.now());
            return (
              <div style={{ borderRadius: 10, border: `1px solid ${withAlpha(C.primary, 0.08)}`, background: '#FAFAFA', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: withAlpha(C.primary, 0.08), display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 900, color: C.primary, lineHeight: 1 }}>{d.getDate()}</span>
                  <span style={{ fontSize: 8.5, fontWeight: 700, color: withAlpha(C.primary, 0.6), textTransform: 'uppercase' }}>{d.toLocaleDateString('nl-NL', { month: 'short' })}</span>
            </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 1 }}>Daarna</div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{next?.title || 'Bijeenkomst'}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>{next?.startTime || d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
              </div>
            );
          })()}

          {/* Snelle acties */}
          <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.12)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>Meer acties</div>
            {([
              { Icon: ConnectionLinkIcon, label: 'Uitnodigingslink kopiëren', action: copyOnboardingInviteLink },
              { Icon: CalendarIcon,       label: 'Event aanmaken',           action: () => setActiveTab('agenda' as any) },
              ...(isOwnerOrOrganizer ? [{ Icon: EditIcon, label: 'Clubpagina bewerken', action: () => setActiveTab('settings' as any) }] : []),
            ] as Array<{ Icon: React.ComponentType<any>; label: string; action: () => void }>).map(({ Icon, label, action }) => (
              <button key={label} onClick={action}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9, marginBottom: 6, background: '#F9FAFB', border: '1px solid #F3F4F6', fontSize: 12.5, fontWeight: 600, color: '#374151', cursor: 'pointer', textAlign: 'left' }}>
                <Icon size={15} weight="regular" color={C.primary} />{label}
                  </button>
            ))}
          </div>

          {/* Activatie widget */}
          {!isActive && (
            <div style={{ background: lighten(C.accent, 0.94), borderRadius: 14, border: `1.5px solid ${withAlpha(C.accent, 0.25)}`, padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#374151' }}>
                  <ActivationIcon size={14} weight="regular" color={C.accent} />Activatievoortgang
                </div>
                <span style={{ fontSize: 13, fontWeight: 800, color: C.accent }}>{progressPct}%</span>
              </div>
              <div style={{ height: 7, background: withAlpha(C.accent, 0.15), borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ height: '100%', width: `${progressPct}%`, background: `linear-gradient(90deg,${C.accent},${darken(C.accent, 0.15)})`, borderRadius: 10, transition: 'width 0.6s ease' }} />
              </div>
              <div style={{ fontSize: 11.5, color: '#6B7280', lineHeight: 1.5 }}>
                <strong style={{ color: C.accent }}>{memberCount}/{minMembers} leden</strong>  nog <strong>{Math.max(0, minMembers - memberCount)}</strong> nodig
              </div>
              <button onClick={() => setActiveTab('invite' as any)}
                style={{ marginTop: 10, width: '100%', padding: '8px 0', borderRadius: 9, background: C.accent, color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: `0 2px 8px ${withAlpha(C.accent, 0.3)}` }}>
                Leden uitnodigen
              </button>
            </div>
          )}

          {/* Over de club */}
          <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.12)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>Over de club</div>
            {([
              { Icon: CompanyIcon,  val: typeLabel },
              { Icon: club.visibility === 'public' ? GlobeIcon : LockIcon,
                val: club.visibility === 'public' ? 'Publieke groep' : 'Besloten groep' },
              club.region          ? { Icon: MapPinIcon,   val: club.region }          : null,
              club.scope           ? { Icon: GlobeIcon,    val: club.scope }            : null,
              club.meetingFrequency ? { Icon: CalendarIcon, val: club.meetingFrequency } : null,
            ] as Array<{ Icon: React.ComponentType<any>; val: string } | null>)
              .filter(Boolean)
              .map((item) => item && (
                <div key={item.val} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', fontSize: 12.5, color: '#374151' }}>
                  <item.Icon size={14} weight="regular" color={C.primary} />{item.val}
              </div>
              ))}
                </div>

          {/* Entity switcher */}
          <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.12)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Wissel van club</div>
            <EntitySwitcher />
                </div>
                  </div>
          </div>
        )}
      </div>
      )}

      {/*  MODALS  */}
      {showInviteGuestModal && (
        <InviteGuestModal clubId={club.id} onClose={() => setShowInviteGuestModal(false)}
          onSuccess={() => { setShowInviteGuestModal(false); loadClubData(activeEntity!.id); }} />
      )}
      {showRequestVisitModal && activeEntity && (
        <RequestClubVisit clubId={club.id} clubName={club.name} entityId={activeEntity.id}
          onClose={() => setShowRequestVisitModal(false)} onSuccess={() => setShowRequestVisitModal(false)} />
      )}

      {/* Invite members modal */}
    </div>
  );

};


//  NextMeetingWidget 
const NextMeetingWidget: React.FC<{ event: any; primary: string; secondary: string }> = ({ event, primary, secondary }) => {
  const [rsvp, setRsvp] = React.useState<'yes' | 'no' | null>(null);
  const evDate = event?.eventDate instanceof Date ? event.eventDate : new Date(event?.eventDate || Date.now());
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(primary, 0.12)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
      <div style={{ background: `linear-gradient(135deg,${primary},${secondary})`, padding: '12px 16px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: withAlpha('#ffffff', 0.7), textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Volgende meeting</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{event?.title || 'Bijeenkomst'}</div>
        <div style={{ fontSize: 12, color: withAlpha('#ffffff', 0.8), marginTop: 3 }}>
          {String.fromCodePoint(0x1F4C5)} {evDate.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
        </div>
      </div>
      <div style={{ padding: '12px 16px' }}>
        {rsvp === null ? (
          <>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>Ben jij erbij?</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setRsvp('yes')} style={{ flex: 1, padding: '8px 0', borderRadius: 8, background: lighten(primary, 0.92), color: primary, border: `1.5px solid ${withAlpha(primary, 0.2)}`, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Ja, erbij</button>
              <button onClick={() => setRsvp('no')} style={{ flex: 1, padding: '8px 0', borderRadius: 8, background: '#FFF5F5', color: '#EF4444', border: '1.5px solid rgba(239,68,68,0.2)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Nee</button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: rsvp === 'yes' ? lighten(primary, 0.9) : '#FFF5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{rsvp === 'yes' ? '+' : 'x'}</div>
                  <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: rsvp === 'yes' ? primary : '#EF4444' }}>{rsvp === 'yes' ? 'Aanwezig bevestigd' : 'Afgemeld'}</div>
              <button onClick={() => setRsvp(null)} style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Wijzigen</button>
            </div>
                  </div>
                )}
      </div>
    </div>
  );
};
// ── Online ledenrij in de linkersidebar ───────────────────────────────────

const OnlineMemberRow: React.FC<{
  presence: { userId: string; userName: string; userPhotoURL?: string; status: string; countryCode?: string };
  currentUserId: string;
  accentColor: string;
}> = ({ presence: p, currentUserId, accentColor }) => {
  type FlowStatus =
    | 'loading'
    | 'none'            // nog geen verzoek
    | 'request_sent'    // verzoek verstuurd, wacht op acceptatie
    | 'request_received'// het andere lid wacht op jou
    | 'accepted'        // verzoek geaccepteerd, online-fase lopend
    | 'physical_pending'// fysieke afspraak gepland, nog niet afgerond
    | 'completed';      // fysieke fase afgerond = volledig SuperKonnected

  const [flowStatus, setFlowStatus] = React.useState<FlowStatus>('loading');
  const [sending, setSending] = React.useState(false);
  const isSelf = p.userId === currentUserId;

  React.useEffect(() => {
    if (isSelf || !currentUserId || !p.userId) { setFlowStatus('none'); return; }
    let cancelled = false;
    (async () => {
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('../../firebase');

        // 1. Check `connections` collectie (fase 2+3 — volledig flow object)
        const [conn1, conn2] = await Promise.all([
          getDocs(query(collection(db, 'connections'), where('user1Id', '==', currentUserId), where('user2Id', '==', p.userId))),
          getDocs(query(collection(db, 'connections'), where('user1Id', '==', p.userId), where('user2Id', '==', currentUserId))),
        ]);
        const connDocs = [...conn1.docs, ...conn2.docs];
        if (connDocs.length > 0) {
          const data = connDocs[0].data();
          const physStatus = data?.physicalMeeting?.status;
          if (physStatus === 'done' || physStatus === 'completed' || data?.phase === 'completed') {
            if (!cancelled) setFlowStatus('completed');
          } else if (physStatus === 'planned' || physStatus === 'scheduled') {
            if (!cancelled) setFlowStatus('physical_pending');
          } else {
            if (!cancelled) setFlowStatus('accepted');
          }
          return;
        }

        // 2. Check `connectionRequests` (fase 1 — beide schema-varianten)
        const [q1, q2, q3, q4] = await Promise.all([
          getDocs(query(collection(db, 'connectionRequests'), where('senderId', '==', currentUserId), where('receiverId', '==', p.userId))),
          getDocs(query(collection(db, 'connectionRequests'), where('senderId', '==', p.userId), where('receiverId', '==', currentUserId))),
          getDocs(query(collection(db, 'connectionRequests'), where('fromUserId', '==', currentUserId), where('toUserId', '==', p.userId))),
          getDocs(query(collection(db, 'connectionRequests'), where('fromUserId', '==', p.userId), where('toUserId', '==', currentUserId))),
        ]);
        if (cancelled) return;

        const fromMe    = [...q1.docs, ...q3.docs];
        const toMe      = [...q2.docs, ...q4.docs];
        const allDocs   = [...fromMe, ...toMe];

        if (allDocs.length === 0) { setFlowStatus('none'); return; }

        const hasAccepted = allDocs.some((d: any) => d.data().status === 'accepted');
        if (hasAccepted) { setFlowStatus('accepted'); return; }

        if (fromMe.some((d: any) => d.data().status === 'pending')) { setFlowStatus('request_sent'); return; }
        if (toMe.some((d: any)   => d.data().status === 'pending')) { setFlowStatus('request_received'); return; }

        setFlowStatus('none');
      } catch {
        if (!cancelled) setFlowStatus('none');
      }
    })();
    return () => { cancelled = true; };
  }, [isSelf, currentUserId, p.userId]);

  const sendRequest = async () => {
    if (!currentUserId || isSelf || flowStatus !== 'none' || sending) return;
    setSending(true);
    try {
      await connectionRequestService.sendConnectionRequest(currentUserId, p.userId);
      setFlowStatus('request_sent');
    } catch (e) {
      console.error('[OnlineMemberRow] Connection request failed:', e);
    } finally {
      setSending(false);
    }
  };

  // Badge configuratie per status
  const badge: { label: string; bg: string; color: string; title: string } | null = (() => {
    switch (flowStatus) {
      case 'request_sent':
        return { label: 'Wacht op reactie', bg: '#FEF3C7', color: '#92400E', title: 'Verzoek verstuurd — wacht op acceptatie' };
      case 'request_received':
        return { label: 'Wil verbinden', bg: '#EFF6FF', color: '#1E40AF', title: 'Dit lid wil met jou verbinden — check je inbox' };
      case 'accepted':
        return { label: 'Online fase', bg: '#ECFDF5', color: '#065F46', title: 'Verbonden — online fase loopt' };
      case 'physical_pending':
        return { label: 'Fysiek gepland', bg: '#F3E8FF', color: '#6B21A8', title: 'Fysieke afspraak gepland — flow bijna klaar' };
      case 'completed':
        return { label: 'SuperKonnected ✓', bg: '#ECFDF5', color: '#065F46', title: 'Volledig verbonden — fysieke fase afgerond' };
      default:
        return null;
    }
  })();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', borderRadius: 10, transition: 'background 0.15s' }}
      onMouseEnter={e => (e.currentTarget.style.background = '#F9FAFB')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      {/* Avatar met online-dot + vlag rechtsboven */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        {p.userPhotoURL ? (
          <img src={p.userPhotoURL} alt={p.userName} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff' }}>
            {p.userName[0]?.toUpperCase()}
          </div>
        )}
        {/* Online-dot linksonder */}
        <span style={{ position: 'absolute', bottom: 1, right: 1, width: 8, height: 8, borderRadius: '50%', background: '#22C55E', border: '1.5px solid #fff' }} />
        {/* Vlag rechtsboven — alleen als bekend */}
        {p.countryCode && (
          <span title={`Land: ${p.countryCode}`} style={{ position: 'absolute', top: -3, right: -3, fontSize: 10, lineHeight: 1, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))' }}>
            {p.countryCode}
          </span>
        )}
                </div>

      {/* Naam + status badge */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: isSelf ? '#9CA3AF' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.userName}{isSelf ? ' (jij)' : ''}
              </div>
        {!isSelf && badge && (
          <div title={badge.title} style={{ display: 'inline-flex', alignItems: 'center', marginTop: 2, fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 20, background: badge.bg, color: badge.color, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {badge.label}
            </div>
        )}
          </div>

      {/* Verbindingsknop — alleen als nog geen verzoek bestaat */}
      {!isSelf && flowStatus === 'none' && (
        <button
          onClick={() => void sendRequest()}
          disabled={sending}
          title="Verbindingsverzoek sturen"
          style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, border: `1.5px solid ${accentColor}`, background: 'white', cursor: sending ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}
          aria-label="Verbindingsverzoek sturen"
        >
          {sending ? (
            <div style={{ width: 10, height: 10, border: `2px solid ${accentColor}`, borderTopColor: 'transparent', borderRadius: '50%' }} />
          ) : (
            <RelationIcon size={13} weight="regular" color={accentColor} />
          )}
        </button>
      )}
          </div>
  );
};

// ── Live meeting kaart ────────────────────────────────────────────────────
const LiveMeetingCard: React.FC<{
  meeting: { roomName: string; startedByName: string; startedAt: Date | null };
  colors: { primary: string; secondary: string; accent: string };
  onJoin: () => void;
}> = ({ meeting, colors: C, onJoin }) => {
  const [elapsed, setElapsed] = React.useState(0);

  React.useEffect(() => {
    if (!meeting.startedAt) return;
    const tick = () => setElapsed(Math.floor((Date.now() - meeting.startedAt!.getTime()) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [meeting.startedAt]);

  const fmt = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
  };

  return (
    <div style={{
      borderRadius: 16,
      overflow: 'hidden',
      background: `linear-gradient(135deg, #064E3B 0%, #065F46 60%, ${C.primary} 100%)`,
      boxShadow: '0 4px 20px rgba(4,120,87,0.35)',
      border: '1px solid rgba(52,211,153,0.25)',
    }}>
      {/* Pulserend licht-effect boven */}
      <div style={{ height: 3, background: 'linear-gradient(90deg, transparent, #34D399, transparent)', animation: 'nc-fadeUp 2s ease-in-out infinite alternate' }} />

      <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* Live indicator + avatar */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(52,211,153,0.2)', border: '2px solid rgba(52,211,153,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MicrophoneIcon size={22} weight="fill" color="#6EE7B7" />
          </div>
          {/* Pulserende ring */}
          <span style={{ position: 'absolute', inset: -4, borderRadius: '50%', border: '2px solid rgba(52,211,153,0.3)', animation: 'nc-fadeUp 1.5s ease-in-out infinite alternate' }} />
          </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#6EE7B7', textTransform: 'uppercase' }}>Nu live</span>
            {meeting.startedAt && (
              <span style={{ fontSize: 11, color: 'rgba(167,243,208,0.7)', fontFamily: 'monospace', fontWeight: 600 }}>
                {fmt(elapsed)}
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 2 }}>
            Online meeting is actief
        </div>
          <div style={{ fontSize: 12, color: 'rgba(167,243,208,0.85)' }}>
            Gestart door <strong style={{ color: '#A7F3D0' }}>{meeting.startedByName}</strong>
        </div>
        </div>

        {/* Join knop */}
        <button
          onClick={onJoin}
          style={{ flexShrink: 0, height: 44, padding: '0 22px', borderRadius: 12, background: '#10B981', border: 'none', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: '0 2px 12px rgba(16,185,129,0.5)', transition: 'transform 0.1s, box-shadow 0.1s' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.transform = 'scale(1.03)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#10B981'; e.currentTarget.style.transform = 'scale(1)'; }}
          aria-label="Online meeting joinen"
        >
          Joinen
        </button>
      </div>
    </div>
  );
};

// ── Standaard clubfuncties (vrij uitbreidbaar door organisator) ───────────
const CLUB_FUNCTIES = [
  'Gasheer / Gastvrouw',
  'Penningmeester',
  'Social Media Manager',
  'Secretaris',
  'Notulist',
  'Voorzitter',
  'Evenementencoördinator',
  'Ledenwerving',
  'Spreker',
  'Mentor',
];

// ── Ledenrij met functie-toewijzing ───────────────────────────────────────
interface MemberRowProps {
  member: ClubInviteMemberRow;
  canManage: boolean;
  colors: { primary: string; secondary: string; accent: string };
  entityId: string;
}
const MemberRow: React.FC<MemberRowProps> = ({ member: m, canManage, colors: C, entityId }) => {
  const [editing, setEditing] = React.useState(false);
  const [functie, setFunctie] = React.useState<string>(m.functie || '');
  const [customFunctie, setCustomFunctie] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const rolLabel = m.role === 'owner' || m.role === 'organizer' ? 'Organisator'
    : m.role === 'board' ? 'Bestuur'
    : m.role === 'va' ? 'VA'
    : 'Lid';

  const saveFunctie = async (value: string) => {
    setSaving(true);
    const docId = `${entityId}_${m.id}`;
    try {
      await setDoc(
        doc(db, 'entityMembers', docId),
        { functie: value || null },
        { merge: true },
      );
      setFunctie(value);
      setEditing(false);
    } catch (e) {
      console.error('Functie opslaan mislukt:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
      {/* Avatar */}
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg,${C.primary},${C.secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
        {(m.name || '?')[0].toUpperCase()}
      </div>

      {/* Naam + email + functie */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          <span style={{ fontSize: 11.5, color: '#6B7280' }}>{m.email}</span>
          {functie && !editing && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(251,191,36,0.1)', color: '#92400E', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 20, padding: '1px 8px', fontSize: 10.5, fontWeight: 700 }}>
              {functie}
              {canManage && (
                <button onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#B45309', lineHeight: 1 }} title="Functie wijzigen">
                  <EditIcon size={10} weight="regular" />
                </button>
              )}
            </span>
          )}
          {canManage && !functie && !editing && (
            <button onClick={() => setEditing(true)}
              style={{ fontSize: 10.5, color: withAlpha(C.primary, 0.5), background: 'none', border: `1px dashed ${withAlpha(C.primary, 0.25)}`, borderRadius: 20, padding: '1px 8px', cursor: 'pointer', fontWeight: 600 }}>
              + Functie toewijzen
            </button>
          )}
        </div>

        {/* Inline functie-editor */}
        {editing && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {CLUB_FUNCTIES.map(f => (
                <button key={f} onClick={() => void saveFunctie(f)} disabled={saving}
                  style={{ padding: '3px 10px', borderRadius: 20, border: `1.5px solid ${f === functie ? C.primary : '#E5E7EB'}`, background: f === functie ? withAlpha(C.primary, 0.08) : '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: f === functie ? C.primary : '#374151' }}>
                  {f}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={customFunctie}
                onChange={e => setCustomFunctie(e.target.value)}
                placeholder="Eigen functie…"
                style={{ flex: 1, height: 28, padding: '0 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12, outline: 'none' }}
              />
              <button onClick={() => void saveFunctie(customFunctie.trim() || '')} disabled={saving || !customFunctie.trim()}
                style={{ height: 28, padding: '0 12px', borderRadius: 8, background: C.accent, color: '#fff', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? '…' : 'OK'}
              </button>
              <button onClick={() => void saveFunctie('')} disabled={saving}
                style={{ height: 28, padding: '0 10px', borderRadius: 8, background: '#F3F4F6', color: '#6B7280', border: 'none', fontSize: 11, cursor: 'pointer' }}>
                Verwijder
              </button>
              <button onClick={() => setEditing(false)}
                style={{ height: 28, padding: '0 10px', borderRadius: 8, background: 'none', border: '1px solid #E5E7EB', color: '#9CA3AF', fontSize: 11, cursor: 'pointer' }}>
                Annuleer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Rol-badge */}
      <span style={{ background: withAlpha(C.primary, 0.08), color: C.primary, border: `1.5px solid ${withAlpha(C.primary, 0.15)}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
        {rolLabel}
      </span>
    </div>
  );
};

// ── Besloten club post-card (compact, reacties inklapbaar) ────────────────
interface ClubPostCardProps {
  post: any;
  currentUser: any;
  colors: { primary: string; secondary: string; accent: string };
  onShareToNewsfeed: (post: any) => Promise<void>;
}
const ClubPostCard: React.FC<ClubPostCardProps> = ({ post: p, currentUser, colors: C, onShareToNewsfeed }) => {
  const [showInteractions, setShowInteractions] = React.useState(false);
  const [commentCount, setCommentCount] = React.useState<number>(
    typeof p.commentsCount === 'number' ? p.commentsCount : 0
  );

  // Realtime comment count — opent wanneer iemand reageert
  React.useEffect(() => {
    if (!p.id) return;
    const unsub = onSnapshot(
      collection(db, 'communityPosts', p.id, 'comments'),
      (snap) => setCommentCount(snap.size),
      () => {} // silent fail
    );
    return () => unsub();
  }, [p.id]);
  const name = p.authorName || p.author || p.authorEmail?.split('@')[0] || 'Lid';
  const initial = name[0].toUpperCase();
  const post = { ...p, userId: p.userId || p.authorId || '', author: name };
  const date = p.createdAt ? new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(p.createdAt)) : '';

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: withAlpha(C.primary, 0.1) }}>
      {/* Header */}
      <div className="flex gap-3 items-start px-4 pt-4 pb-3">
        {p.authorPhoto ? (
          <img src={p.authorPhoto} alt={name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" style={{ border: `2px solid ${withAlpha(C.primary, 0.18)}` }} />
        ) : (
          <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-black text-white" style={{ background: `linear-gradient(135deg,${C.primary},${C.secondary})` }}>
            {initial}
              </div>
        )}
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold" style={{ color: '#111827' }}>{name}</span>
              {date && <span className="text-xs" style={{ color: '#9CA3AF' }}>{date}</span>}
              {/* Auteur-rol badge */}
              {(p.authorRole === 'owner' || p.authorRole === 'organizer') && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: withAlpha(C.primary, 0.12), color: C.primary }}>
                  Organisator
                </span>
              )}
              {p.authorRole === 'board' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(251,191,36,0.12)', color: '#B45309' }}>
                  Bestuur
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: withAlpha(C.primary, 0.06), color: withAlpha(C.primary, 0.6) }}>
                <LockIcon size={8} weight="fill" /> Besloten
              </span>
            </div>
          <p className="text-sm leading-relaxed mt-1.5 whitespace-pre-line" style={{ color: '#374151' }}>{p.content}</p>
        </div>
      </div>

      {/* Media */}
      {(p.imageUrl || p.mediaUrl) && (
        <img src={p.imageUrl || p.mediaUrl} alt="" className="w-full block" style={{ maxHeight: 280, objectFit: 'cover' }} />
      )}

      {/* Compact actiebalk */}
      <div className="flex items-center gap-1 px-4 py-2 border-t" style={{ borderColor: withAlpha(C.primary, 0.07) }}>
        {/* Reacties toggle — met zichtbare count */}
        <button
          onClick={() => setShowInteractions(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
          style={showInteractions
            ? { background: withAlpha(C.primary, 0.12), color: C.primary }
            : commentCount > 0
              ? { background: withAlpha(C.primary, 0.08), color: C.primary }
              : { background: withAlpha(C.primary, 0.06), color: '#6B7280' }}
        >
          💬
          {commentCount > 0
            ? <span>{commentCount} {commentCount === 1 ? 'reactie' : 'reacties'}</span>
            : <span>Reageer</span>
          }
          {commentCount > 0 && !showInteractions && (
            <span className="ml-0.5 w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.primary }} />
          )}
        </button>

        <div className="flex-1" />

        {/* Deel naar nieuwsfeed + social */}
        <GroupFeedActions post={p} onShareToNewsfeed={onShareToNewsfeed} />
      </div>

      {/* PostInteractions — bevat zelf respect + mutual respect + reacties */}
      {showInteractions && (
        <div className="px-4 pb-3 border-t" style={{ borderColor: withAlpha(C.primary, 0.07) }}>
          <PostInteractions
            post={post}
            currentUserId={currentUser?.uid || ''}
            currentUserName={currentUser?.displayName || currentUser?.email?.split('@')[0] || ''}
            currentUser={currentUser}
            userProfile={currentUser}
            onShare={onShareToNewsfeed}
            showDelete={post.userId === currentUser?.uid}
          />
        </div>
      )}
    </div>
  );
};

// ── Club instellingen — kleuren + roltoewijzingen ────────────────────────
const SAGE_PALETTE = [
  { label: 'Sage',          value: '#7A9E8E' },
  { label: 'Diep groen',    value: '#2D5E4F' },
  { label: 'Eucalyptus',    value: '#4A8B7A' },
  { label: 'Mint',          value: '#5BA08A' },
  { label: 'Koraal',        value: '#E07A5F' },
  { label: 'Amber',         value: '#C9A84C' },
  { label: 'Lavendel',      value: '#7B7FC4' },
  { label: 'Aubergine',     value: '#6B3F6E' },
  { label: 'Staal blauw',   value: '#3A6B96' },
  { label: 'Antraciet',     value: '#3D4451' },
];

// ── Herbruikbaar accordion-blok voor instellingen ────────────────────────
const SettingsAccordion: React.FC<{
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #F3F4F6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
      {/* Header — klikbaar */}
        <button
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 1 }}>{subtitle}</div>}
        </div>
        {/* Pijl */}
        <svg
          width="16" height="16" viewBox="0 0 16 16" fill="none"
          style={{ flexShrink: 0, color: '#9CA3AF', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        </button>

      {/* Inhoud — inklapbaar */}
      {open && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid #F9FAFB' }}>
          {children}
      </div>
      )}
    </div>
  );
};

// ── Mijn Pitch tab — auto-populate vanuit profiel, foto upload, publiceren ────
const MijnPitchTab: React.FC<{
  entityId: string;
  clubId: string;
  userId: string;
  displayName: string;
  colors: { primary: string; secondary: string; accent: string };
}> = ({ entityId, clubId, userId, displayName, colors: C }) => {
  const [title,        setTitle]        = React.useState('');
  const [message,      setMessage]      = React.useState('');
  const [searchQ,      setSearchQ]      = React.useState('');
  const [recTarget,    setRecTarget]    = React.useState('');
  const [recReason,    setRecReason]    = React.useState('');
  const [photoUrl,     setPhotoUrl]     = React.useState('');
  const [logoUrl,      setLogoUrl]      = React.useState('');
  const [published,    setPublished]    = React.useState(false);
  const [templateBg,   setTemplateBg]   = React.useState('');
  const [saving,       setSaving]       = React.useState(false);
  const [publishing,   setPublishing]   = React.useState(false);
  const [savedMsg,     setSavedMsg]     = React.useState('');
  const [loaded,       setLoaded]       = React.useState(false);
  const [uploading,    setUploading]    = React.useState<'photo' | 'logo' | null>(null);
  const [showPreview,  setShowPreview]  = React.useState(false);

  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const logoInputRef  = React.useRef<HTMLInputElement>(null);

  // Laad bestaande draft + auto-populate vanuit userProfiles als draft leeg is
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const draftSnap = await getDoc(doc(db, 'meetingPitchDrafts', `${entityId}_${userId}`));
        // Haal ook clubtemplate op voor de preview
        const clubSnap = await getDoc(doc(db, 'networkClubs', clubId));
        if (!cancelled && clubSnap.exists()) {
          const ov = (clubSnap.data() as any).presentationOverlaySettings || {};
          setTemplateBg(ov.templateBackgroundUrl || '');
        }
        if (!cancelled && draftSnap.exists()) {
          const d = draftSnap.data() as any;
          setTitle(d.title || '');
          setMessage(d.message || '');
          setSearchQ(d.searchQuestion || '');
          setRecTarget(d.recTarget || '');
          setRecReason(d.recReason || '');
          setPhotoUrl(d.photoUrl || '');
          setLogoUrl(d.logoUrl || '');
          setPublished(!!d.published);
        } else if (!cancelled) {
          // Auto-populate vanuit userProfile bij eerste opening
          const profileSnap = await getDoc(doc(db, 'userProfiles', userId));
          if (!cancelled && profileSnap.exists()) {
            const p = profileSnap.data() as any;
            setTitle(p.jobTitle || p.profession || '');
            setMessage(p.bio || p.about || '');
            setSearchQ(p.lookingFor || '');
            setPhotoUrl(p.photoURL || p.profilePicture || '');
            setLogoUrl(p.companyLogoUrl || p.logoUrl || '');
          }
        }
      } catch { /* stil */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [entityId, userId, clubId]);

  const handleImageUpload = async (file: File, kind: 'photo' | 'logo') => {
    if (!file.type.startsWith('image/')) { alert('Kies een afbeeldingsbestand.'); return; }
    setUploading(kind);
    try {
      const compressed = await compressForCover(file);
      const fileRef = storageRef(storage, `pitchMedia/${entityId}/${userId}/${kind}_${Date.now()}_${compressed.name}`);
      await uploadBytes(fileRef, compressed);
      const url = await getDownloadURL(fileRef);
      if (kind === 'photo') setPhotoUrl(url);
      else setLogoUrl(url);
      // Meteen opslaan in draft
      await setDoc(doc(db, 'meetingPitchDrafts', `${entityId}_${userId}`), {
        entityId, userId, presenterName: displayName,
        ...(kind === 'photo' ? { photoUrl: url } : { logoUrl: url }),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.error(`${kind} upload mislukt:`, e);
      alert('Upload mislukt. Probeer opnieuw.');
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async (andPublish = false) => {
    andPublish ? setPublishing(true) : setSaving(true);
    try {
      await setDoc(doc(db, 'meetingPitchDrafts', `${entityId}_${userId}`), {
        entityId,
        userId,
        presenterName: displayName,
        title:          title.trim(),
        message:        message.trim(),
        searchQuestion: searchQ.trim(),
        recTarget:      recTarget.trim(),
        recReason:      recReason.trim(),
        photoUrl,
        logoUrl,
        published:      andPublish ? true : published,
        publishedAt:    andPublish ? serverTimestamp() : undefined,
        updatedAt:      serverTimestamp(),
      }, { merge: true });
      if (andPublish) setPublished(true);
      setSavedMsg(andPublish ? 'Pitch gepubliceerd — zichtbaar voor de hele club!' : 'Opgeslagen als concept');
      setTimeout(() => setSavedMsg(''), 4000);
    } catch (e) {
      console.error('Pitch opslaan mislukt:', e);
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  if (!loaded) return (
    <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Laden…</div>
  );

  const inp: React.CSSProperties = {
    width: '100%', height: 40, padding: '0 12px', borderRadius: 9,
    border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none',
    boxSizing: 'border-box', color: '#374151', background: '#FAFAFA',
  };
  const ta: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 9,
    border: '1.5px solid #E5E7EB', fontSize: 13, outline: 'none',
    resize: 'none', boxSizing: 'border-box', color: '#374151', background: '#FAFAFA',
  };
  const lbl: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Status badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Mijn pitch</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
            Auto-ingevuld vanuit jouw profiel — pas aan en publiceer voor de meeting.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {published && (
            <span style={{ background: '#ECFDF5', color: '#065F46', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '4px 10px', border: '1px solid #A7F3D0' }}>
              Gepubliceerd
            </span>
          )}
          <button
            onClick={() => setShowPreview(p => !p)}
            style={{ fontSize: 11, fontWeight: 700, color: C.primary, background: withAlpha(C.primary, 0.08), border: `1px solid ${withAlpha(C.primary, 0.2)}`, borderRadius: 8, padding: '5px 12px', cursor: 'pointer' }}>
            {showPreview ? 'Verberg preview' : 'Beamer preview'}
          </button>
        </div>
      </div>

      {/* Beamer-kaart preview */}
      {showPreview && (
        <div style={{ borderRadius: 12, overflow: 'hidden', border: `2px solid ${withAlpha(C.primary, 0.18)}`, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          <div style={{ background: '#111', padding: '4px 10px', fontSize: 10, color: '#9CA3AF', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
            Beamer preview — zo ziet jouw kaart eruit op het scherm
          </div>
          <div style={{
            position: 'relative', width: '100%', aspectRatio: '16/5',
            background: templateBg ? `url(${templateBg}) center/cover no-repeat` : `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
            display: 'flex', alignItems: 'center', padding: '0 24px', gap: 20, boxSizing: 'border-box',
          }}>
            {/* Avatar */}
            <div style={{ width: 72, height: 72, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.8)', overflow: 'hidden', flexShrink: 0, background: withAlpha(C.accent, 0.4), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {photoUrl ? (
                <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: 28, color: '#fff', fontWeight: 800 }}>{displayName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            {/* Tekst */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.4)', lineHeight: 1.2 }}>{displayName}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 600, marginTop: 2 }}>{title || <em style={{ opacity: 0.5 }}>Pitch titel…</em>}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4, maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{message || <em style={{ opacity: 0.5 }}>Kernboodschap…</em>}</div>
            </div>
            {/* Bedrijfslogo */}
            {logoUrl && (
              <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.6)', flexShrink: 0, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4, boxSizing: 'border-box' }} />
              </div>
            )}
          </div>
          {searchQ && (
            <div style={{ background: C.accent, color: '#fff', padding: '6px 24px', fontSize: 12, fontWeight: 700 }}>
              Zoekt: {searchQ}
            </div>
          )}
        </div>
      )}

      {/* Foto + logo upload */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Profielfoto */}
        <div style={{ background: '#F9FAFB', border: '1.5px dashed #D1D5DB', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', border: '2px solid #E5E7EB', background: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {photoUrl ? <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 24, color: '#9CA3AF' }}>👤</span>}
          </div>
          <button
            onClick={() => photoInputRef.current?.click()}
            disabled={uploading === 'photo'}
            style={{ fontSize: 11, fontWeight: 700, color: C.primary, background: withAlpha(C.primary, 0.08), border: `1px solid ${withAlpha(C.primary, 0.2)}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', opacity: uploading === 'photo' ? 0.5 : 1 }}>
            {uploading === 'photo' ? 'Uploaden…' : 'Foto wijzigen'}
          </button>
          <span style={{ fontSize: 10.5, color: '#9CA3AF', textAlign: 'center' }}>Profielfoto op beamer</span>
          <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) void handleImageUpload(f, 'photo'); if (photoInputRef.current) photoInputRef.current.value = ''; }} />
        </div>
        {/* Bedrijfslogo */}
        <div style={{ background: '#F9FAFB', border: '1.5px dashed #D1D5DB', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 60, height: 60, borderRadius: 10, overflow: 'hidden', border: '2px solid #E5E7EB', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {logoUrl ? <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4, boxSizing: 'border-box' }} /> : <span style={{ fontSize: 24, color: '#9CA3AF' }}>🏢</span>}
          </div>
          <button
            onClick={() => logoInputRef.current?.click()}
            disabled={uploading === 'logo'}
            style={{ fontSize: 11, fontWeight: 700, color: C.primary, background: withAlpha(C.primary, 0.08), border: `1px solid ${withAlpha(C.primary, 0.2)}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', opacity: uploading === 'logo' ? 0.5 : 1 }}>
            {uploading === 'logo' ? 'Uploaden…' : 'Logo wijzigen'}
          </button>
          <span style={{ fontSize: 10.5, color: '#9CA3AF', textAlign: 'center' }}>Bedrijfslogo op beamer</span>
          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) void handleImageUpload(f, 'logo'); if (logoInputRef.current) logoInputRef.current.value = ''; }} />
        </div>
      </div>

      {/* Tekstvelden */}
      <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${withAlpha(C.primary, 0.1)}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={lbl}>Pitch titel</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Bijv. Ondernemer in duurzame energie" style={inp} maxLength={80} />
        </div>
        <div>
          <label style={lbl}>Kernboodschap <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(1–3 zinnen)</span></label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
            placeholder="Wat doe je, voor wie, wat maakt jou uniek?" style={ta} maxLength={300} />
          <div style={{ fontSize: 10.5, color: '#9CA3AF', textAlign: 'right', marginTop: 2 }}>{message.length}/300</div>
        </div>
        <div>
          <label style={lbl}>Wat zoek ik? <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(matchmaking)</span></label>
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
            placeholder="Bijv. warm contact met directeur van bedrijf X, investeerder, klant…"
            style={inp} maxLength={120} />
        </div>

        {/* Aanbeveling */}
        <div style={{ background: withAlpha(C.accent, 0.06), borderRadius: 10, border: `1px solid ${withAlpha(C.accent, 0.2)}`, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.accent, marginBottom: 10 }}>
            ★ Aanbeveling voor een lid <span style={{ fontWeight: 400, color: '#9CA3AF', fontSize: 11 }}>(optioneel)</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input value={recTarget} onChange={e => setRecTarget(e.target.value)}
              placeholder="Naam van het lid dat je aanbeveelt"
              style={{ ...inp, border: `1.5px solid ${withAlpha(C.accent, 0.3)}` }} maxLength={60} />
            <textarea value={recReason} onChange={e => setRecReason(e.target.value)} rows={2}
              placeholder="Waarom beveel je deze persoon aan? (1–2 zinnen)"
              style={{ ...ta, border: `1.5px solid ${withAlpha(C.accent, 0.3)}` }} maxLength={200} />
          </div>
          <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 6 }}>
            De aanbeveling wordt live getoond tijdens jouw pitch.
          </div>
        </div>
      </div>

      {/* Feedback */}
      {savedMsg && (
        <div style={{ padding: '8px 14px', background: published ? '#ECFDF5' : '#EFF6FF', borderRadius: 8, color: published ? '#065F46' : '#1E40AF', fontSize: 12, fontWeight: 600, border: `1px solid ${published ? '#A7F3D0' : '#BFDBFE'}` }}>
          ✓ {savedMsg}
        </div>
      )}

      {/* Knoppen */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => void handleSave(false)}
          disabled={saving || publishing}
          style={{ flex: 1, height: 42, borderRadius: 10, border: `1.5px solid ${withAlpha(C.primary, 0.3)}`, background: '#fff', color: C.primary, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (saving || publishing) ? 0.5 : 1 }}>
          {saving ? 'Opslaan…' : 'Opslaan als concept'}
        </button>
        <button
          onClick={() => void handleSave(true)}
          disabled={saving || publishing || (!title.trim() && !message.trim())}
          style={{ flex: 2, height: 42, borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (saving || publishing || (!title.trim() && !message.trim())) ? 0.5 : 1, boxShadow: `0 3px 10px ${withAlpha(C.accent, 0.4)}` }}>
          {publishing ? 'Publiceren…' : published ? '↑ Opnieuw publiceren' : 'Opslaan & Publiceren'}
        </button>
      </div>
      <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
        Publiceren maakt je pitch zichtbaar voor de hele club en plaatst hem in de bijeenkomsttijdlijn.
      </div>
    </div>
  );
};

// ── Beheerder: bijeenkomst tijdlijn-editor (gepubliceerde pitches ordenen) ─────
type PitchEntry = {
  id: string;
  userId: string;
  presenterName: string;
  title: string;
  message: string;
  searchQuestion: string;
  photoUrl?: string;
  logoUrl?: string;
  published: boolean;
  timelineOrder?: number;
  durationMinutes?: number;
};

const MeetingTimelineEditor: React.FC<{
  entityId: string;
  clubId: string;
  colors: { primary: string; secondary: string; accent: string };
}> = ({ entityId, colors: C }) => {
  const [entries,  setEntries]  = React.useState<PitchEntry[]>([]);
  const [loading,  setLoading]  = React.useState(true);
  const [saving,   setSaving]   = React.useState(false);
  const [saved,    setSaved]    = React.useState(false);
  const [dragIdx,  setDragIdx]  = React.useState<number | null>(null);
  const [overIdx,  setOverIdx]  = React.useState<number | null>(null);

  // Laad gepubliceerde pitches voor deze club
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(db, 'meetingPitchDrafts'),
          where('entityId', '==', entityId),
          where('published', '==', true)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const raw: PitchEntry[] = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
        // Sorteer op bestaande timelineOrder, daarna alfabetisch
        raw.sort((a, b) => {
          if (a.timelineOrder != null && b.timelineOrder != null) return a.timelineOrder - b.timelineOrder;
          if (a.timelineOrder != null) return -1;
          if (b.timelineOrder != null) return 1;
          return (a.presenterName || '').localeCompare(b.presenterName || '');
        });
        setEntries(raw);
      } catch { /* stil */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [entityId]);

  const moveEntry = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    setEntries(prev => {
      const arr = [...prev];
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return arr;
    });
  };

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver  = (e: React.DragEvent, idx: number) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop      = (idx: number) => {
    if (dragIdx !== null) moveEntry(dragIdx, idx);
    setDragIdx(null);
    setOverIdx(null);
  };
  const handleDragEnd   = () => { setDragIdx(null); setOverIdx(null); };

  const saveOrder = async () => {
    setSaving(true);
    try {
      await Promise.all(entries.map((e, i) =>
        setDoc(doc(db, 'meetingPitchDrafts', e.id), { timelineOrder: i, durationMinutes: e.durationMinutes ?? 3 }, { merge: true })
      ));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Tijdlijn opslaan mislukt:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateDuration = (idx: number, mins: number) => {
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, durationMinutes: mins } : e));
  };

  const removeFromTimeline = async (entry: PitchEntry) => {
    if (!window.confirm(`${entry.presenterName} verwijderen uit de tijdlijn?`)) return;
    await setDoc(doc(db, 'meetingPitchDrafts', entry.id), { published: false }, { merge: true });
    setEntries(prev => prev.filter(e => e.id !== entry.id));
  };

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Laden…</div>;

  const totalMinutes = entries.reduce((s, e) => s + (e.durationMinutes ?? 3), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Bijeenkomst tijdlijn</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
            Sleep de kaarten om de volgorde te bepalen. Gepubliceerde pitches van leden verschijnen hier automatisch.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 600 }}>
            {entries.length} pitches · {totalMinutes} min totaal
          </span>
          <button
            onClick={() => void saveOrder()}
            disabled={saving || entries.length === 0}
            style={{ height: 36, padding: '0 16px', borderRadius: 9, border: 'none', background: C.accent, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (saving || entries.length === 0) ? 0.5 : 1, boxShadow: `0 2px 8px ${withAlpha(C.accent, 0.4)}` }}>
            {saving ? 'Opslaan…' : saved ? '✓ Opgeslagen' : 'Volgorde opslaan'}
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        <div style={{ padding: '32px 24px', textAlign: 'center', color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12, border: '1.5px dashed #E5E7EB' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Nog geen gepubliceerde pitches</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Leden publiceren hun pitch via het tabblad "Mijn pitch".</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map((entry, idx) => (
            <div
              key={entry.id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={e => handleDragOver(e, idx)}
              onDrop={() => handleDrop(idx)}
              onDragEnd={handleDragEnd}
              style={{
                background: dragIdx === idx ? withAlpha(C.primary, 0.06) : '#fff',
                border: `1.5px solid ${overIdx === idx && dragIdx !== idx ? C.accent : withAlpha(C.primary, 0.12)}`,
                borderRadius: 12, padding: '12px 14px',
                display: 'flex', alignItems: 'center', gap: 12,
                cursor: 'grab', boxShadow: dragIdx === idx ? `0 4px 16px ${withAlpha(C.primary, 0.15)}` : '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                opacity: dragIdx === idx ? 0.7 : 1,
              }}>
              {/* Volgorde + drag handle */}
              <div style={{ width: 28, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 800 }}>#{idx + 1}</div>
                <div style={{ fontSize: 14, color: '#D1D5DB', lineHeight: 1 }}>⠿</div>
              </div>
              {/* Avatar */}
              <div style={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden', border: `2px solid ${withAlpha(C.primary, 0.2)}`, background: withAlpha(C.primary, 0.1), flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {entry.photoUrl
                  ? <img src={entry.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 16, fontWeight: 800, color: C.primary }}>{(entry.presenterName || '?').charAt(0).toUpperCase()}</span>
                }
              </div>
              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.presenterName}</div>
                <div style={{ fontSize: 11.5, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.title || <em>Geen titel</em>}</div>
              </div>
              {/* Duur */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <label style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>min</label>
                <input
                  type="number" min={1} max={30} value={entry.durationMinutes ?? 3}
                  onChange={e => updateDuration(idx, Math.max(1, Math.min(30, parseInt(e.target.value) || 3)))}
                  onClick={e => e.stopPropagation()}
                  style={{ width: 46, height: 30, borderRadius: 7, border: '1.5px solid #E5E7EB', fontSize: 12, textAlign: 'center', outline: 'none', color: '#374151' }}
                />
              </div>
              {/* Verwijder knop */}
              <button
                onClick={() => void removeFromTimeline(entry)}
                title="Verwijderen uit tijdlijn"
                style={{ width: 28, height: 28, borderRadius: 7, border: '1.5px solid #FCA5A5', background: '#FEF2F2', color: '#EF4444', fontSize: 14, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, color: '#9CA3AF', borderTop: '1px solid #F3F4F6', paddingTop: 10 }}>
        De tijdlijn wordt automatisch geladen in de fysieke bijeenkomst wanneer je op "Fysiek" klikt.
      </div>
    </div>
  );
};

const ClubSettingsSection: React.FC<{ club: NetworkClub; onUpdate: () => void }> = ({ club, onUpdate }) => {
  const [primary,   setPrimary]   = React.useState<string>((club as any).primaryColor   || '#2D5E4F');
  const [secondary, setSecondary] = React.useState<string>((club as any).secondaryColor || '#1B3A4B');
  const [accent,    setAccent]    = React.useState<string>((club as any).accentColor    || '#7A9E8E');
  const [saving,    setSaving]    = React.useState(false);
  const [saved,     setSaved]     = React.useState(false);

  const [websiteInput, setWebsiteInput] = React.useState<string>((club as any).website || '');
  const [websiteSaving, setWebsiteSaving] = React.useState(false);
  const [websiteSaved, setWebsiteSaved] = React.useState(false);
  const [websiteError, setWebsiteError] = React.useState('');

  const [storyInput, setStoryInput] = React.useState<string>((club as any).description || '');
  const [storySaving, setStorySaving] = React.useState(false);
  const [storySaved, setStorySaved] = React.useState(false);

  React.useEffect(() => {
    setWebsiteInput(((club as any).website as string) || '');
    setWebsiteError('');
    setStoryInput(((club as any).description as string) || '');
  }, [club.id, (club as any).website, (club as any).description]);

  // ── Overlay instellingen ──────────────────────────────────────────────────
  const ovDefaults = (club as any).presentationOverlaySettings || {};
  const [ovBannerColor,       setOvBannerColor]       = React.useState<string>(ovDefaults.bannerColor       || '#1D4ED8');
  const [ovBannerTextColor,   setOvBannerTextColor]   = React.useState<string>(ovDefaults.bannerTextColor   || '#FFFFFF');
  const [ovTimerColor,        setOvTimerColor]        = React.useState<string>(ovDefaults.timerColor        || '#FFFFFF');
  const [ovNumberColor,       setOvNumberColor]       = React.useState<string>(ovDefaults.numberColor       || '#FFFFFF');
  const [ovLogoMode,          setOvLogoMode]          = React.useState<string>(ovDefaults.logoMode          || 'alternating');
  const [ovLogoSwitchSec,     setOvLogoSwitchSec]     = React.useState<number>(ovDefaults.logoSwitchIntervalSec  || 10);
  const [ovLogoFadeSec,       setOvLogoFadeSec]       = React.useState<number>(ovDefaults.logoFadeDurationSec    || 1.5);
  const [ovBannerContent,     setOvBannerContent]     = React.useState<string>(ovDefaults.bannerContent     || 'searchQuestion');
  const [ovNumberPrefix,      setOvNumberPrefix]      = React.useState<string>(ovDefaults.numberPrefix      || 'NR:');
  const [ovSaving,  setOvSaving]  = React.useState(false);
  const [ovSaved,   setOvSaved]   = React.useState(false);

  // ── Template-achtergrond upload (PDF/Canva export als PNG/JPG) ─────────────
  const [templateBgUrl,      setTemplateBgUrl]      = React.useState<string>(ovDefaults.templateBackgroundUrl || '');
  const [templateUploading,  setTemplateUploading]  = React.useState(false);
  const templateInputRef = React.useRef<HTMLInputElement>(null);

  const handleTemplateUpload = async (file: File) => {
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      alert('Upload een afbeelding (PNG/JPG) of exporteer je PDF als afbeelding vanuit Canva.');
      return;
    }
    setTemplateUploading(true);
    try {
      const { ref: sRef, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const { storage: sStorage } = await import('../../firebase');
      const fileRef = sRef(sStorage, `groupHeaders/${club.id}/template_bg_${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      setTemplateBgUrl(url);
      await updateDoc(doc(db, 'networkClubs', club.id), {
        'presentationOverlaySettings.templateBackgroundUrl': url,
      });
    } catch (e) {
      console.error('Template upload mislukt:', e);
      alert('Upload mislukt. Probeer opnieuw.');
    } finally {
      setTemplateUploading(false);
    }
  };

  const saveColors = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'networkClubs', club.id), {
        primaryColor: primary,
        secondaryColor: secondary,
        accentColor: accent,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onUpdate();
    } catch (e) {
      console.error('Kleuren opslaan mislukt:', e);
    } finally {
      setSaving(false);
    }
  };

  const saveStory = async () => {
    setStorySaving(true);
    try {
      const trimmed = storyInput.trim();
      await updateDoc(
        doc(db, 'networkClubs', club.id),
        trimmed ? { description: trimmed } : { description: deleteField() }
      );
      setStorySaved(true);
      setTimeout(() => setStorySaved(false), 2500);
      onUpdate();
    } catch (e) {
      console.error('Clubverhaal opslaan mislukt:', e);
    } finally {
      setStorySaving(false);
    }
  };

  const saveWebsite = async () => {
    setWebsiteError('');
    const normalized = normalizeNetworkClubWebsiteUrl(websiteInput);
    if (websiteInput.trim() && !normalized) {
      setWebsiteError('Voer een geldige URL in (bijv. https://jouwclub.nl).');
      return;
    }
    setWebsiteSaving(true);
    try {
      await updateDoc(
        doc(db, 'networkClubs', club.id),
        normalized ? { website: normalized } : { website: deleteField() }
      );
      setWebsiteSaved(true);
      setTimeout(() => setWebsiteSaved(false), 2500);
      onUpdate();
    } catch (e) {
      console.error('Website opslaan mislukt:', e);
      setWebsiteError('Opslaan mislukt. Controleer je rechten of probeer opnieuw.');
    } finally {
      setWebsiteSaving(false);
    }
  };

  const saveOverlaySettings = async () => {
    setOvSaving(true);
    try {
      await updateDoc(doc(db, 'networkClubs', club.id), {
        presentationOverlaySettings: {
          bannerColor:          ovBannerColor,
          bannerTextColor:      ovBannerTextColor,
          timerColor:           ovTimerColor,
          numberColor:          ovNumberColor,
          logoMode:             ovLogoMode,
          logoSwitchIntervalSec: Math.max(3, Math.min(60, ovLogoSwitchSec)),
          logoFadeDurationSec:   Math.max(0.3, Math.min(3, ovLogoFadeSec)),
          bannerContent:        ovBannerContent,
          numberPrefix:         ovNumberPrefix.trim() || 'NR:',
          templateBackgroundUrl: templateBgUrl || null,
        },
      });
      setOvSaved(true);
      setTimeout(() => setOvSaved(false), 2500);
      onUpdate();
    } catch (e) {
      console.error('Overlay-instellingen opslaan mislukt:', e);
    } finally {
      setOvSaving(false);
    }
  };

  const preview = { primary, secondary, accent };

  const colorRow = (label: string, value: string, onChange: (v: string) => void, hint?: string) => (
    <div key={label} style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151' }}>{label}</div>
          {hint && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{hint}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="color" value={value} onChange={e => onChange(e.target.value)}
            style={{ width: 34, height: 34, borderRadius: 8, border: '1.5px solid #E5E7EB', cursor: 'pointer', padding: 2 }} />
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#6B7280', background: '#F9FAFB', padding: '3px 8px', borderRadius: 6, border: '1px solid #E5E7EB' }}>{value}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      <SettingsAccordion
        title="Clubverhaal"
        subtitle="Verhaal en filosofie van de club — zichtbaar op het tabblad Ons verhaal"
        defaultOpen
      >
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Verhaal &amp; filosofie</div>
          <textarea
            value={storyInput}
            onChange={(e) => setStoryInput(e.target.value)}
            placeholder="Schrijf hier het verhaal van jullie club: wie jullie zijn, wat jullie drijft, de waarden en de missie. Leden lezen dit op het tabblad Ons verhaal."
            rows={6}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 10,
              border: '1.5px solid #E5E7EB',
              fontSize: 13,
              lineHeight: 1.65,
              resize: 'vertical',
              boxSizing: 'border-box',
              outline: 'none',
              color: '#374151',
            }}
          />
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5 }}>Leeg laten om te verwijderen.</div>
          {storySaved && (
            <div style={{ marginTop: 10, padding: '8px 14px', background: '#ECFDF5', borderRadius: 8, color: '#065F46', fontSize: 12, fontWeight: 600, border: '1px solid #A7F3D0' }}>
              ✓ Clubverhaal opgeslagen
            </div>
          )}
          <button
            type="button"
            onClick={() => void saveStory()}
            disabled={storySaving}
            style={{ marginTop: 12, height: 38, padding: '0 20px', borderRadius: 10, background: accent, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: storySaving ? 0.6 : 1 }}
          >
            {storySaving ? 'Opslaan\u2026' : 'Verhaal opslaan'}
          </button>
        </div>
      </SettingsAccordion>

      <SettingsAccordion
        title="Website van de club"
        subtitle="Zichtbaar op het tabblad Ons verhaal als “Onze website” (insluiten of nieuw tabblad)"
        defaultOpen
      >
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151', marginBottom: 6 }}>URL</div>
          <input
            type="url"
            value={websiteInput}
            onChange={(e) => setWebsiteInput(e.target.value)}
            placeholder="https://jouworganisatie.nl"
            style={{
              width: '100%',
              height: 40,
              padding: '0 12px',
              borderRadius: 10,
              border: '1.5px solid #E5E7EB',
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6, lineHeight: 1.45 }}>
            Leeg laten om te verwijderen. Leden zien een toggle alleen als hier een geldige URL staat.
          </div>
          {websiteError && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#FEF2F2', borderRadius: 8, color: '#B91C1C', fontSize: 12, fontWeight: 600 }}>
              {websiteError}
            </div>
          )}
          {websiteSaved && (
            <div style={{ marginTop: 10, padding: '8px 14px', background: '#ECFDF5', borderRadius: 8, color: '#065F46', fontSize: 12, fontWeight: 600, border: '1px solid #A7F3D0' }}>
              ✓ Website opgeslagen
            </div>
          )}
          <button
            type="button"
            onClick={() => void saveWebsite()}
            disabled={websiteSaving}
            style={{
              marginTop: 12,
              height: 38,
              padding: '0 20px',
              borderRadius: 10,
              background: accent,
              border: 'none',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              opacity: websiteSaving ? 0.6 : 1,
            }}
          >
            {websiteSaving ? 'Opslaan\u2026' : 'Website opslaan'}
          </button>
        </div>
      </SettingsAccordion>

      {/* ── Clubkleuren (inklapbaar) ── */}
      <SettingsAccordion
        title="Clubkleuren"
        subtitle="Pas de header, knoppen en accentkleuren aan"
      >
        {/* Kleurpreview */}
        <div style={{ height: 44, borderRadius: 10, background: `linear-gradient(135deg,${preview.secondary},${preview.primary} 55%,${withAlpha(preview.accent, 0.8)})`, margin: '14px 0', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', padding: '0 14px', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', opacity: 0.85 }}>Voorbeeld header</span>
          <span style={{ marginLeft: 'auto', background: preview.accent, borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#fff' }}>Knop</span>
        </div>

        {[
          { label: 'Primaire kleur', value: primary, set: setPrimary, hint: 'Hoofdkleur van de header en tekst' },
          { label: 'Secundaire kleur', value: secondary, set: setSecondary, hint: 'Achtergrond gradient' },
          { label: 'Accentkleur', value: accent, set: setAccent, hint: 'Knoppen, badges, highlights' },
        ].map(({ label, value, set, hint }) => (
          <div key={label} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151' }}>{label}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{hint}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="color" value={value} onChange={e => set(e.target.value)}
                  style={{ width: 34, height: 34, borderRadius: 8, border: '1.5px solid #E5E7EB', cursor: 'pointer', padding: 2 }} />
                <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#6B7280', background: '#F9FAFB', padding: '3px 8px', borderRadius: 6, border: '1px solid #E5E7EB' }}>{value}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {SAGE_PALETTE.map(sw => (
                <button key={sw.value} onClick={() => set(sw.value)} title={sw.label}
                  style={{ width: 22, height: 22, borderRadius: 6, background: sw.value, border: sw.value === value ? '2.5px solid #111' : '2.5px solid transparent', cursor: 'pointer', flexShrink: 0 }}
                  aria-label={sw.label}
                />
              ))}
            </div>
          </div>
        ))}

        {saved && (
          <div style={{ padding: '8px 14px', background: '#ECFDF5', borderRadius: 8, color: '#065F46', fontSize: 12, fontWeight: 600, border: '1px solid #A7F3D0', marginBottom: 12 }}>
            ✓ Kleuren opgeslagen
          </div>
        )}

        <button onClick={() => void saveColors()} disabled={saving}
          style={{ height: 38, padding: '0 20px', borderRadius: 10, background: accent, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Opslaan\u2026' : 'Kleuren opslaan'}
        </button>
      </SettingsAccordion>

      {/* ── Presentatie-overlay instellingen ── */}
      <SettingsAccordion
        title="Presentatie-overlay"
        subtitle="Kleuren, logo-modus en banner voor de video-meeting overlay"
      >
        {/* Live preview */}
        <div style={{ position: 'relative', height: 80, borderRadius: 10, background: 'linear-gradient(135deg,#0f172a,#1e293b)', margin: '12px 0', overflow: 'hidden', border: '1px solid #334155' }}>
          <div style={{ position: 'absolute', top: 10, left: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: ovBannerColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: ovBannerTextColor }}>LOGO</div>
            <div style={{ fontSize: 11, fontWeight: 800, color: ovTimerColor }}>00:59 SEC</div>
          </div>
          <div style={{ position: 'absolute', top: 12, right: 12, textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#fff', opacity: 0.7 }}>NEXT</div>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: '#334155', marginTop: 2, marginLeft: 'auto' }} />
            <div style={{ fontSize: 8, color: ovNumberColor, fontWeight: 700 }}>{ovNumberPrefix} 3</div>
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: ovBannerColor, padding: '5px 12px' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: ovBannerTextColor, letterSpacing: 0.5 }}>ZOEK VRAAG: WARM CONTACT MET BEDRIJF X</div>
          </div>
        </div>

        {/* Banner */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Banner</div>
        {colorRow('Bannerkleur', ovBannerColor, setOvBannerColor, 'Achtergrondkleur van de onderste balk')}
        {colorRow('Bannertekstkleur', ovBannerTextColor, setOvBannerTextColor)}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Banner-inhoud</div>
          <select value={ovBannerContent} onChange={e => setOvBannerContent(e.target.value)}
            style={{ width: '100%', height: 36, padding: '0 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 12.5, background: '#fff', color: '#374151' }}>
            <option value="searchQuestion">Zoek vraag (matchmaking)</option>
            <option value="nameCompany">Naam + bedrijf</option>
            <option value="message">Kernboodschap</option>
            <option value="title">Pitch titel</option>
          </select>
        </div>

        {/* Timer */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, margin: '12px 0 10px' }}>Timer</div>
        {colorRow('Timerkleur', ovTimerColor, setOvTimerColor, 'Kleur van de afteller')}

        {/* Nummering */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, margin: '12px 0 10px' }}>Nummering</div>
        {colorRow('Nummerkleur', ovNumberColor, setOvNumberColor)}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Prefix tekst</div>
          <input value={ovNumberPrefix} onChange={e => setOvNumberPrefix(e.target.value)} maxLength={10}
            placeholder="NR:" style={{ width: '100%', height: 36, padding: '0 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 12.5, boxSizing: 'border-box' }} />
          <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 3 }}>Bijv. "NR:", "Spreker", "#"</div>
        </div>

        {/* Logo modus */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, margin: '12px 0 10px' }}>Logo</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Logo-modus</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { value: 'club', label: 'Altijd club' },
              { value: 'member', label: 'Altijd lid' },
              { value: 'alternating', label: 'Afwisselend' },
            ].map(opt => (
              <button key={opt.value} onClick={() => setOvLogoMode(opt.value)}
                style={{ flex: 1, height: 34, borderRadius: 8, border: `1.5px solid ${ovLogoMode === opt.value ? '#1D4ED8' : '#E5E7EB'}`, background: ovLogoMode === opt.value ? '#EFF6FF' : '#fff', color: ovLogoMode === opt.value ? '#1D4ED8' : '#6B7280', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {ovLogoMode === 'alternating' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Wisselinterval (sec)</div>
              <input type="number" min={3} max={60} value={ovLogoSwitchSec} onChange={e => setOvLogoSwitchSec(Number(e.target.value))}
                style={{ width: '100%', height: 34, padding: '0 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 12.5, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>3–60 sec (standaard: 10)</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Fade-duur (sec)</div>
              <input type="number" min={0.3} max={3} step={0.1} value={ovLogoFadeSec} onChange={e => setOvLogoFadeSec(Number(e.target.value))}
                style={{ width: '100%', height: 34, padding: '0 10px', borderRadius: 8, border: '1.5px solid #E5E7EB', fontSize: 12.5, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>0.3–3 sec (standaard: 1.5)</div>
            </div>
          </div>
        )}

        {ovSaved && (
          <div style={{ padding: '8px 14px', background: '#ECFDF5', borderRadius: 8, color: '#065F46', fontSize: 12, fontWeight: 600, border: '1px solid #A7F3D0', marginBottom: 12 }}>
            ✓ Overlay-instellingen opgeslagen
          </div>
        )}

        {/* Template-achtergrond (PDF/Canva export) */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, margin: '12px 0 10px' }}>Template achtergrond</div>
        <input type="file" accept="image/*" ref={templateInputRef} className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleTemplateUpload(f); if (templateInputRef.current) templateInputRef.current.value = ''; }} />
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 8, lineHeight: 1.5 }}>
            Upload een PNG/JPG vanuit Canva of een ander ontwerpprogramma. Wordt gebruikt als achtergrond voor de <strong>fysieke presentatie</strong> als een lid geen eigen foto/video heeft geüpload.
          </div>
          {templateBgUrl && (
            <div style={{ marginBottom: 8, position: 'relative', display: 'inline-block' }}>
              <img src={templateBgUrl} alt="Template preview" style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E7EB' }} />
              <button onClick={() => setTemplateBgUrl('')}
                style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
          )}
          <button onClick={() => templateInputRef.current?.click()} disabled={templateUploading}
            style={{ height: 36, padding: '0 16px', borderRadius: 9, border: '2px dashed #D1D5DB', background: '#F9FAFB', fontSize: 12, color: '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: templateUploading ? 0.6 : 1 }}>
            {templateUploading ? 'Uploaden…' : templateBgUrl ? '📷 Achtergrond wijzigen' : '📷 Achtergrond uploaden'}
          </button>
          <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 4 }}>Aanbevolen: exporteer je Canva template als PNG op 1920×1080.</div>
        </div>

        <button onClick={() => void saveOverlaySettings()} disabled={ovSaving}
          style={{ height: 38, padding: '0 20px', borderRadius: 10, background: accent, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: ovSaving ? 0.6 : 1 }}>
          {ovSaving ? 'Opslaan\u2026' : 'Instellingen opslaan'}
        </button>
      </SettingsAccordion>

    </div>
  );
};

export default NetworkClubProfileHub;
